"use client"

import { nanoid } from "nanoid"
import { useSyncExternalStore } from "react"

import {
  getPlatformDefaultAstrologer,
  getPlatformDefaultAstrologerId,
} from "@/lib/astrologer-default"
import { getStartupRoute } from "@/lib/onboarding-flow"
import type { StartupRouteInput } from "@/lib/onboarding-flow"
import {
  computeUtcOffsetForTimezone,
} from "@/lib/timezone"
import { clearClientCaches } from "@/lib/client-cache"
import { ConsentCancelledError, requireAiConsent, showAiConsentDisclosure } from "@/lib/ai-consent"
import { mergeRefreshedConversation } from "@/lib/conversation-refresh"
import { clearAppQueryClient } from "@/lib/query-client"
import { ApiError, apiBaseUrl, apiDelete, apiGet, apiMutate } from "@/lib/workspace-api"
import {
  recordTracewayWebSocketEvent,
} from "@/lib/traceway-breadcrumbs"
import { enqueueActivityEvent } from "@/lib/activity-events"
import { useAuthStore } from "@/stores/auth-store"
import { reduceSocketEvent } from "./core/turn-reducer"
import type { SocketEnvelope } from "./core/socket-protocol"
import { createSocketManager } from "./core/socket-manager"
import type { SocketLike } from "./core/socket-manager"
import type { ClientEvent } from "./core/socket-protocol"
import { attemptRefreshingSession, signOut } from "supertokens-auth-react/recipe/session"

import type {
  BirthTimeStatus,
  PersonChartSummary,
  Person,
  Astrologer,
  ConversationSource,
  ConversationTurn,
  Conversation,
  WorkspaceSettings,
  WorkspaceHydrationStatus,
  WorkspaceSummaryHydrationStatus,
  WorkspaceState,
  CreditsResponse,
  ConversationSummaryItem,
  ConversationSummaryResponse,
  ConversationMessageItem,
  ConversationMessagesResponse,
  ConversationMessageTurn,
  PersonInput,
  ConversationInput,
  SocketStatus,
} from "./core/workspace-types"

export type * from "./core/workspace-types"

export function getAstrologerSuggestedSendBalancePaise(astrologer: Astrologer | null | undefined) {
  if (!astrologer) {
    return null
  }

  const ceilingThreshold =
    typeof astrologer.turnCeilingPaise === "number" && astrologer.turnCeilingPaise > 0
      ? astrologer.turnCeilingPaise
      : null

  return ceilingThreshold
}

const STORAGE_KEY_PREFIX = "askjyotish.frontend.workspace-state.v4"
const LEGACY_STORAGE_KEYS = [
  "askjyotish.frontend.workspace-state.v3",
  "askjyotish.frontend-vercel.workspace-state.v3",
]

function getAuthenticatedUserId() {
  return useAuthStore.getState().user?.user_id ?? null
}

function getWorkspaceStorageKey(userId: string | null) {
  if (!userId) {
    return null
  }

  return `${STORAGE_KEY_PREFIX}.${encodeURIComponent(userId)}`
}

export function clearWorkspaceStorage() {
  if (typeof window === "undefined") {
    return
  }

  const keysToRemove: string[] = []
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key) {
      continue
    }
    if (key.startsWith(STORAGE_KEY_PREFIX) || LEGACY_STORAGE_KEYS.includes(key)) {
      keysToRemove.push(key)
    }
  }

  for (const key of keysToRemove) {
    window.localStorage.removeItem(key)
  }

  for (const legacyKey of LEGACY_STORAGE_KEYS) {
    window.localStorage.removeItem(legacyKey)
  }
}
const PEOPLE: Person[] = [
  {
    id: "rahul",
    name: "Rahul",
    relationshipToOwner: "self",
    birthName: "Rahul Kumar",
    birthDate: "1994-08-14",
    birthTime: "10:42",
    birthPlace: "Delhi, India",
    birthTimezone: "Asia/Kolkata",
    gender: "male",
    birthTimeTier: 0,
    birthTimeStatus: "exact",
    birthTimeConfidence: "Exact / strong confidence",
    birthTimeSource: "Birth certificate",
    computedCharts: ["D1", "D2", "D3", "D4", "D7", "D9", "D10", "D12", "D24", "D30"],
    readingMode: "d1_d9_varga_mode",
    avatarColor: "#7c6ef0",
    notes: "Career and finance focused.",
    isDefault: true,
  },
  {
    id: "priya",
    name: "Priya",
    relationshipToOwner: "sister",
    birthName: "Priya Sharma",
    birthDate: "1998-03-21",
    birthTime: "07:10",
    birthPlace: "Jaipur, India",
    birthTimezone: "Asia/Kolkata",
    gender: "female",
    birthTimeTier: 2,
    birthTimeStatus: "approximate",
    birthTimeConfidence: "Approximate / review needed",
    birthTimeSource: "Family memory",
    computedCharts: ["D1"],
    readingMode: "d1_only_mode",
    avatarColor: "#e8609a",
    notes: "Relationship and travel questions.",
    isDefault: false,
  },
  {
    id: "amit",
    name: "Amit",
    relationshipToOwner: "friend",
    birthName: "Amit Verma",
    birthDate: "1991-11-07",
    birthTime: "unknown",
    birthPlace: "Lucknow, India",
    birthTimezone: "Asia/Kolkata",
    gender: "male",
    birthTimeTier: null,
    birthTimeStatus: "unknown",
    birthTimeConfidence: "Unknown birth time",
    birthTimeSource: "Not available yet",
    computedCharts: [],
    readingMode: "moon_fallback_mode",
    avatarColor: "#2db87f",
    notes: "Health and timing questions.",
    isDefault: false,
  },
  {
    id: "ananya",
    name: "Ananya",
    relationshipToOwner: "spouse",
    birthName: "Ananya Rao",
    birthDate: "1996-05-02",
    birthTime: "18:35",
    birthPlace: "Pune, India",
    birthTimezone: "Asia/Kolkata",
    gender: "female",
    birthTimeTier: 0,
    birthTimeStatus: "exact",
    birthTimeConfidence: "Exact / verified",
    birthTimeSource: "Hospital record",
    computedCharts: ["D1", "D2", "D3", "D4", "D7", "D9", "D10", "D12", "D24", "D30"],
    readingMode: "d1_d9_varga_mode",
    avatarColor: "#f07c3a",
    notes: "Marriage and relocation.",
    isDefault: false,
  },
]

function nowLabel() {
  return new Date().toISOString()
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
}

function titleFromQuestion(question: string) {
  const trimmed = question.trim()
  if (!trimmed) return "New conversation"
  const firstLine = trimmed.split(/\n+/)[0] ?? trimmed
  return firstLine.length > 64 ? `${firstLine.slice(0, 61)}...` : firstLine
}

function buildSources(person: Person) {
  return [
    {
      title: `${person.name} chart notes`,
      href: "https://example.com/chart-notes",
    },
    {
      title: "Timing heuristics",
      href: "https://example.com/timing-heuristics",
    },
  ]
}

function buildReasoning(question: string, astrologer: Astrologer, person: Person) {
  const q = question.toLowerCase()
  if (q.includes("promotion") || q.includes("job") || q.includes("career")) {
    return `I am checking ${person.name}'s career timing, then weighing whether ${astrologer.displayName} would push for a move, a negotiation, or a patience-first strategy.`
  }
  if (q.includes("marriage") || q.includes("relationship") || q.includes("partner")) {
    return `I am reading the relationship axis for ${person.name}, then comparing the likely timing window against ${astrologer.displayName}'s practical decision style.`
  }
  if (q.includes("money") || q.includes("finance") || q.includes("investment")) {
    return `I am balancing stability versus expansion for ${person.name} and keeping the recommendation concrete enough to act on this month.`
  }
  if (q.includes("health") || q.includes("wellness")) {
    return `I am favoring caution, routine, and observability before making any strong claims about health-related timing.`
  }
  return `I am combining the person's baseline chart context with ${astrologer.displayName}'s reading style to keep the answer precise and useful.`
}

function buildAnswer(question: string, astrologer: Astrologer, person: Person) {
  const q = question.toLowerCase()
  if (q.includes("promotion") || q.includes("job") || q.includes("career")) {
    return `${astrologer.displayName} sees a favorable career window for ${person.name} this year, but the strongest move is preparation rather than a sudden leap. Tighten your role narrative, keep a written record of wins, and be ready to ask for a title or compensation reset when momentum is visible.`
  }
  if (q.includes("marriage") || q.includes("relationship") || q.includes("partner")) {
    return `${astrologer.displayName} would treat this as a conversation about readiness first. The pattern suggests a serious relationship can stabilize, but it improves fastest when expectations are explicit and the other person is given a clear timeline.`
  }
  if (q.includes("money") || q.includes("finance") || q.includes("investment")) {
    return `${astrologer.displayName} would keep the money story conservative for now. Favor a measured portfolio, avoid impulsive risk, and revisit the larger move after the next stable income cycle closes.`
  }
  if (q.includes("travel")) {
    return `${astrologer.displayName} would green-light travel with planning discipline. The timing looks better for short, purposeful trips than open-ended movement.`
  }
  if (q.includes("health") || q.includes("wellness")) {
    return `${astrologer.displayName} would keep this grounded in routine: sleep, food, and stress reduction first, then use the chart only as a timing layer on top.`
  }
  return `${astrologer.displayName} reads this as a practical timing question for ${person.name}. The pattern says the answer will improve if you keep the next step small, measurable, and tied to the current month rather than the whole year.`
}

function buildSuggestions(question: string) {
  const q = question.toLowerCase()
  if (q.includes("career")) {
    return ["Should I switch jobs?", "How should I negotiate my next raise?", "What month looks strongest?"]
  }
  if (q.includes("relationship") || q.includes("marriage")) {
    return ["Is this the right person?", "What should I ask next?", "When does it stabilize?"]
  }
  if (q.includes("finance")) {
    return ["Should I invest now?", "What should I avoid?", "What is the safer timing window?"]
  }
  return ["What is the strongest next step?", "Can you narrow the timing window?", "What should I watch for?"]
}

function createTurn(question: string, astrologer: Astrologer, person: Person): ConversationTurn {
  const createdAt = nowLabel()
  return {
    id: nanoid(),
    question,
    answer: buildAnswer(question, astrologer, person),
    detailedAnswer: "",
    reasoning: "",
    sources: buildSources(person),
    suggestions: buildSuggestions(question),
    status: "complete",
    progressMessages: [],
    cancelRequested: false,
    amountDebitedPaise: null,
    availableBalancePaise: null,
    createdAt,
    answerMessageId: undefined,
    assistantFeedback: null,
  }
}

function createConversationSummary(question: string, person: Person, astrologer: Astrologer) {
  return `${person.name} • ${astrologer.displayName} • ${titleFromQuestion(question)}`
}

function dedupeConversationsById(conversations: Conversation[]) {
  const seen = new Set<string>()
  const unique: Conversation[] = []

  for (let index = conversations.length - 1; index >= 0; index -= 1) {
    const conversation = conversations[index]
    if (seen.has(conversation.id)) {
      continue
    }
    seen.add(conversation.id)
    unique.push(conversation)
  }

  return unique.reverse()
}

function sortConversationsByRecency(conversations: Conversation[]) {
  return [...conversations].sort(
    (left, right) => right.updatedAt.localeCompare(left.updatedAt) || right.id.localeCompare(left.id)
  )
}

function compareConversationRecency(
  left: Pick<Conversation, "updatedAt" | "id">,
  right: Pick<Conversation, "updatedAt" | "id">
) {
  return left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id)
}

function mergeConversationSummaries(
  currentConversations: Conversation[],
  summaries: ConversationSummaryItem[],
  fallbackAstrologerId: string | null,
  options?: { personId?: string; reconcileMissing?: boolean }
) {
  const conversationsById = new Map(
    currentConversations.map((conversation) => [conversation.id, conversation] as const)
  )
  const summaryIds = new Set(summaries.map((summary) => summary.conversation_id))
  const cutoff = summaries.at(-1) ?? null

  for (const summary of summaries) {
    const existing = conversationsById.get(summary.conversation_id)
    conversationsById.set(summary.conversation_id, {
      id: summary.conversation_id,
      personId: summary.subject_id,
      astrologerId:
        summary.astrologer_id ??
        existing?.astrologerId ??
        fallbackAstrologerId ??
        "",
      astrologerDisplayName:
        summary.astrologer_display_name ?? existing?.astrologerDisplayName ?? null,
      astrologerAvatarUrl:
        summary.astrologer_avatar_url ?? existing?.astrologerAvatarUrl ?? null,
      astrologerSlug: summary.astrologer_slug ?? existing?.astrologerSlug ?? null,
      matchId: summary.match_id ?? existing?.matchId ?? null,
      partnerSubjectId: summary.partner_subject_id ?? existing?.partnerSubjectId ?? null,
      partnerName: summary.partner_name ?? existing?.partnerName ?? null,
      title: summary.title ?? existing?.title ?? "Conversation",
      summary: summary.title ?? existing?.summary ?? "Conversation",
      updatedAt: summary.last_message_at,
      turns: existing?.turns ?? [],
      historyLoadStatus:
        existing?.turns && existing.turns.length > 0
          ? existing.historyLoadStatus ?? "loaded"
          : existing?.historyLoadStatus ?? "idle",
    })
  }

  if (options?.reconcileMissing && options.personId) {
    for (const conversation of currentConversations) {
      if (conversation.personId !== options.personId) {
        continue
      }
      if (summaryIds.has(conversation.id)) {
        continue
      }
      if (
        !cutoff ||
        compareConversationRecency(conversation, {
          updatedAt: cutoff.last_message_at,
          id: cutoff.conversation_id,
        }) >= 0
      ) {
        conversationsById.delete(conversation.id)
      }
    }
  }

  return sortConversationsByRecency([...conversationsById.values()])
}

function dedupeStrings(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))]
}

function buildConversationSummariesPath(
  personId: string,
  options?: { limit?: number; cursor?: string | null }
) {
  const query = new URLSearchParams({
    subject_id: personId,
    limit: String(options?.limit ?? 10),
  })
  const cursor = options?.cursor?.trim()
  if (cursor) {
    query.set("cursor", cursor)
  }
  return `/v1/conversations?${query.toString()}`
}

function mergeConversationSummariesIntoState(
  summaries: ConversationSummaryItem[],
  personId: string,
  options?: { updateActiveConversation?: boolean; reconcileMissing?: boolean }
) {
  const current = getWorkspaceState()
  const conversations = mergeConversationSummaries(
    current.conversations,
    summaries,
    getPlatformDefaultAstrologerId(current.astrologers),
    {
      personId,
      reconcileMissing: options?.reconcileMissing ?? false,
    }
  )
  const shouldUpdateActiveConversation = options?.updateActiveConversation ?? true
  const nextActiveConversationId = shouldUpdateActiveConversation
    ? conversations.some((conversation) => conversation.id === current.activeConversationId)
      ? current.activeConversationId
      : conversations[0]?.id ?? ""
    : current.activeConversationId

  setState({
    ...current,
    conversations,
    activeConversationId: nextActiveConversationId,
  })
}

export async function loadConversationSummariesPage(
  personId: string,
  options?: {
    limit?: number
    cursor?: string | null
    updateStore?: boolean
    updateActiveConversation?: boolean
    sessionVersion?: number
    shouldUpdateStore?: () => boolean
    reconcileMissing?: boolean
  }
) {
  const normalizedPersonId = personId.trim()
  if (!normalizedPersonId) {
    return {
      items: [],
      pagination: {
        next_cursor: null,
        has_more: false,
      },
    } satisfies ConversationSummaryResponse
  }

  const response = await apiGet<ConversationSummaryResponse>(
    buildConversationSummariesPath(normalizedPersonId, options)
  )

  if (
    typeof options?.sessionVersion === "number" &&
    workspaceSessionVersion !== options.sessionVersion
  ) {
    return response.data
  }

  if (options?.updateStore !== false && (options?.shouldUpdateStore?.() ?? true)) {
    mergeConversationSummariesIntoState(response.data.items, normalizedPersonId, {
      updateActiveConversation: options?.updateActiveConversation,
      reconcileMissing: options?.cursor == null,
    })
  }

  return response.data
}

async function runWithConcurrencyLimit<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
) {
  const queue = [...items]
  const workerCount = Math.max(1, Math.min(queue.length, Math.floor(limit)))
  const workers = Array.from({ length: workerCount }, async () => {
    while (queue.length > 0) {
      const item = queue.shift()
      if (item === undefined) {
        return
      }
      await worker(item)
    }
  })

  await Promise.all(workers)
}

function dedupeTurnsById(turns: ConversationTurn[]) {
  const seen = new Set<string>()
  const unique: ConversationTurn[] = []

  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index]
    if (seen.has(turn.id)) {
      continue
    }
    seen.add(turn.id)
    unique.push(turn)
  }

  return unique.reverse()
}

function createInitialState(): WorkspaceState {
  return {
    people: [],
    astrologers: [],
    conversations: [],
    selectedPersonId: "",
    activeConversationId: "",
    settings: {
      defaultAstrologerId: "",
      defaultSubjectId: "",
      showDetailedAnalysis: true,
      matchmakingEnabled: false,
    },
    credits: 0,
    workspaceHydrationStatus: "idle",
    workspaceSummaryHydrationStatus: "idle",
  }
}

const INITIAL_STATE = createInitialState()

let state: WorkspaceState = cloneState(INITIAL_STATE)
let hydrated = false
let hydratedForUserId: string | null = null
let remoteBootstrapInFlight: Promise<void> | null = null
let startupBootstrapInFlight: Promise<StartupBootstrapSnapshot> | null = null
let workspaceSessionVersion = 0
const conversationSummaryRequests = new Map<string, Promise<Conversation | null>>()
const conversationMessageRequests = new Map<string, Promise<void>>()
const assistantFeedbackMutationQueues = new Map<string, Promise<unknown>>()
const assistantFeedbackMutationVersions = new Map<string, number>()
const assistantFeedbackConfirmedValues = new Map<string, "up" | "down" | null>()
const loadedConversationSummaryPersonIds = new Set<string>()
const listeners = new Set<() => void>()

type PendingSocketSubmit = {
  conversationId: string
  content: string
  localTurnId: string
  serverTurnId?: string
  retryOfTurnId?: string
  sourceFailedTurnId?: string
}

let workspaceSocketStatus: SocketStatus = "closed"
let workspaceSocketManager: ReturnType<typeof createSocketManager> | null = null
const pendingSocketSubmits = new Map<string, PendingSocketSubmit>()
const socketStatusListeners = new Set<() => void>()
const MAX_RETRY_ATTEMPTS = 2
const SESSION_EXPIRED_ERROR_MESSAGE = "Your session has expired. Please sign in again."
let sessionExpiryInFlight: Promise<void> | null = null

function cloneState(next: WorkspaceState): WorkspaceState {
  const normalized = normalizeWorkspaceState(next)
  return {
    ...normalized,
    people: [...normalized.people],
    astrologers: [...normalized.astrologers],
    conversations: normalized.conversations.map((conversation) => ({
      ...conversation,
      composerDraft: conversation.composerDraft ?? "",
      composerSourceFailedTurnId: conversation.composerSourceFailedTurnId ?? undefined,
      turns: conversation.turns.map((turn) => ({
        ...turn,
        sources: turn.sources.map((source) => ({ ...source })),
        suggestions: [...turn.suggestions],
        progressMessages: [...turn.progressMessages],
      })),
    })),
    settings: { ...normalized.settings },
  }
}

function stripTransientTurnState(turn: ConversationTurn): ConversationTurn | null {
  if (
    turn.status === "thinking" ||
    turn.status === "streaming" ||
    turn.livePhase === "thinking" ||
    turn.livePhase === "streaming" ||
    turn.livePhase === "settling"
  ) {
    return null
  }

  return {
    ...turn,
    progressMessages: [],
  }
}

function normalizeLivePhase(
  livePhase: ConversationTurn["livePhase"] | undefined,
): ConversationTurn["livePhase"] | undefined {
  return livePhase === "thinking" || livePhase === "streaming" || livePhase === "settling"
    ? livePhase
    : undefined
}

function isCancelableLiveTurn(turn: ConversationTurn) {
  return (turn.status === "thinking" || turn.status === "streaming") && turn.livePhase !== "settling"
}

async function expireAuthenticatedSession() {
  if (sessionExpiryInFlight) {
    return sessionExpiryInFlight
  }

  sessionExpiryInFlight = (async () => {
    useAuthStore.getState().setGuest()
    resetWorkspaceState()
    clearAppQueryClient()
    await clearClientCaches()

    try {
      await signOut()
    } catch {
      // Ignore sign-out failures so the client still returns to a clean guest state.
    } finally {
      sessionExpiryInFlight = null
    }
  })()

  return sessionExpiryInFlight
}

function sanitizeWorkspaceStateForPersistence(
  next: WorkspaceState,
): Omit<WorkspaceState, "workspaceHydrationStatus" | "workspaceSummaryHydrationStatus"> {
  const normalized = cloneState(next)
  const {
    workspaceHydrationStatus: _workspaceHydrationStatus,
    workspaceSummaryHydrationStatus: _workspaceSummaryHydrationStatus,
    socketEventIds: _socketEventIds,
    ...persisted
  } = normalized
  return {
    ...persisted,
    conversations: persisted.conversations.map((conversation) => ({
      ...conversation,
      turns: conversation.turns
        .map(stripTransientTurnState)
        .filter((turn): turn is ConversationTurn => turn !== null),
    })),
  }
}

function normalizeWorkspaceState(next: Partial<WorkspaceState>): WorkspaceState {
  const people = Array.isArray(next.people) ? next.people : []
  const astrologers = Array.isArray(next.astrologers) ? next.astrologers : []
  const conversations = Array.isArray(next.conversations) ? dedupeConversationsById(next.conversations) : []
  const workspaceHydrationStatus: WorkspaceHydrationStatus =
    next.workspaceHydrationStatus === "hydrating" ||
    next.workspaceHydrationStatus === "hydrated" ||
    next.workspaceHydrationStatus === "error"
      ? next.workspaceHydrationStatus
      : "idle"
  const workspaceSummaryHydrationStatus: WorkspaceSummaryHydrationStatus =
    next.workspaceSummaryHydrationStatus === "warming" ||
    next.workspaceSummaryHydrationStatus === "ready" ||
    next.workspaceSummaryHydrationStatus === "error"
      ? next.workspaceSummaryHydrationStatus
      : "idle"

  return {
    ...createInitialState(),
    ...next,
    people,
    astrologers,
    conversations: sortConversationsByRecency(
      conversations.map((conversation) => {
        const turns = Array.isArray(conversation.turns) ? conversation.turns : []
        return {
          ...conversation,
          sourceFailedTurnId:
            typeof conversation.sourceFailedTurnId === "string"
              ? conversation.sourceFailedTurnId
              : undefined,
          composerDraft: typeof conversation.composerDraft === "string" ? conversation.composerDraft : "",
          composerSourceFailedTurnId:
            typeof conversation.composerSourceFailedTurnId === "string"
              ? conversation.composerSourceFailedTurnId
              : undefined,
          historyLoadStatus:
            conversation.historyLoadStatus === "loading" ||
            conversation.historyLoadStatus === "loaded" ||
            conversation.historyLoadStatus === "error"
              ? conversation.historyLoadStatus
              : "idle",
          turns: dedupeTurnsById(
            turns.map((turn) => ({
              ...turn,
              sources: Array.isArray(turn.sources) ? turn.sources : [],
              suggestions: Array.isArray(turn.suggestions) ? turn.suggestions : [],
              detailedAnswer: typeof turn.detailedAnswer === "string" ? turn.detailedAnswer : "",
              reasoning: typeof turn.reasoning === "string" ? turn.reasoning : "",
              progressMessages: Array.isArray(turn.progressMessages)
                ? turn.progressMessages.filter((message): message is string => typeof message === "string")
                : [],
              errorCode: typeof turn.errorCode === "string" ? turn.errorCode : undefined,
              errorMessage: typeof turn.errorMessage === "string" ? turn.errorMessage : undefined,
              errorDetails:
                turn.errorDetails && typeof turn.errorDetails === "object"
                  ? (turn.errorDetails as Record<string, unknown>)
                  : undefined,
              cancelRequested: typeof turn.cancelRequested === "boolean" ? turn.cancelRequested : false,
              serverTurnId: typeof turn.serverTurnId === "string" ? turn.serverTurnId : undefined,
              requestMessageId: typeof turn.requestMessageId === "string" ? turn.requestMessageId : undefined,
              retryOfTurnId: typeof turn.retryOfTurnId === "string" ? turn.retryOfTurnId : undefined,
              retryable: typeof turn.retryable === "boolean" ? turn.retryable : undefined,
              retryCount:
                typeof turn.retryCount === "number" && Number.isFinite(turn.retryCount)
                  ? turn.retryCount
                  : 0,
              amountDebitedPaise:
                typeof turn.amountDebitedPaise === "number" ? turn.amountDebitedPaise : null,
              availableBalancePaise:
                typeof turn.availableBalancePaise === "number" ? turn.availableBalancePaise : null,
              followUpState:
                turn.followUpState === "retried" ||
                turn.followUpState === "edited" ||
                turn.followUpState === "continued" ||
                turn.followUpState === "new_chat"
                  ? turn.followUpState
                  : undefined,
              followUpTurnId:
                typeof turn.followUpTurnId === "string" ? turn.followUpTurnId : undefined,
              followUpConversationId:
                typeof turn.followUpConversationId === "string"
                  ? turn.followUpConversationId
                  : undefined,
              sourceFailedTurnId:
                typeof turn.sourceFailedTurnId === "string" ? turn.sourceFailedTurnId : undefined,
              answerMessageId:
                typeof turn.answerMessageId === "string" ? turn.answerMessageId : undefined,
              livePhase: normalizeLivePhase(turn.livePhase),
              assistantFeedback:
                turn.assistantFeedback === "up" ||
                turn.assistantFeedback === "down" ||
                turn.assistantFeedback === null
                  ? turn.assistantFeedback
                  : undefined,
              followUpActionState:
                turn.followUpActionState === "edit" ||
                turn.followUpActionState === "copy" ||
                turn.followUpActionState === "new_chat"
                  ? turn.followUpActionState
                  : undefined,
            }))
          ),
        }
      })
    ),
    settings: {
      defaultAstrologerId: next.settings?.defaultAstrologerId ?? "",
      defaultSubjectId: next.settings?.defaultSubjectId ?? "",
      showDetailedAnalysis: true,
      matchmakingEnabled: next.settings?.matchmakingEnabled === true,
    },
    selectedPersonId: next.selectedPersonId ?? "",
    activeConversationId: next.activeConversationId ?? "",
    credits: typeof next.credits === "number" ? next.credits : 0,
    workspaceHydrationStatus,
    workspaceSummaryHydrationStatus,
  }
}

function hydrate() {
  if (typeof window === "undefined") {
    return
  }

  const userId = getAuthenticatedUserId()
  if (!userId) {
    return
  }

  if (hydrated && hydratedForUserId === userId) {
    return
  }

  const switchingUsers = hydratedForUserId !== null && hydratedForUserId !== userId
  if (switchingUsers) {
    remoteBootstrapInFlight = null
    startupBootstrapInFlight = null
    conversationSummaryRequests.clear()
    conversationMessageRequests.clear()
    assistantFeedbackMutationQueues.clear()
    assistantFeedbackMutationVersions.clear()
    assistantFeedbackConfirmedValues.clear()
    loadedConversationSummaryPersonIds.clear()
    pendingSocketSubmits.clear()
    state = cloneState(INITIAL_STATE)
  }

  const storageKey = getWorkspaceStorageKey(userId)
  if (!storageKey) {
    return
  }

  hydrated = true
  hydratedForUserId = userId

  let raw = window.localStorage.getItem(storageKey)
  if (!raw && !switchingUsers) {
    for (const legacyKey of LEGACY_STORAGE_KEYS) {
      raw = window.localStorage.getItem(legacyKey)
      if (raw) {
        window.localStorage.removeItem(legacyKey)
        try {
          const parsed = JSON.parse(raw) as Partial<WorkspaceState>
          state = normalizeWorkspaceState(parsed)
          syncAssistantFeedbackConfirmedValuesFromState(state)
          window.localStorage.setItem(storageKey, JSON.stringify(sanitizeWorkspaceStateForPersistence(state)))
        } catch {
          // Ignore invalid migrated state and continue with defaults.
        }
        break
      }
    }
  }
  if (!raw) {
    state = cloneState(INITIAL_STATE)
    assistantFeedbackConfirmedValues.clear()
    return
  }

  try {
    const parsed = JSON.parse(raw) as Partial<WorkspaceState>
    if (parsed && typeof parsed === "object") {
      state = normalizeWorkspaceState(parsed)
      syncAssistantFeedbackConfirmedValuesFromState(state)
      window.localStorage.setItem(storageKey, JSON.stringify(sanitizeWorkspaceStateForPersistence(state)))
    }
  } catch {
    // Ignore invalid persisted state and continue with defaults.
  }
}

function persist() {
  if (typeof window === "undefined") {
    return
  }

  const storageKey = getWorkspaceStorageKey(getAuthenticatedUserId())
  if (!storageKey) {
    return
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(sanitizeWorkspaceStateForPersistence(state)))
  } catch {
    // Ignore persistence failures.
  }
}

function emit() {
  persist()
  for (const listener of listeners) {
    listener()
  }
}

function emitWithoutPersist() {
  for (const listener of listeners) {
    listener()
  }
}

function emitSocketStatus() {
  for (const listener of socketStatusListeners) {
    listener()
  }
}

function setWorkspaceSocketStatus(next: SocketStatus) {
  if (workspaceSocketStatus === next) {
    return
  }
  workspaceSocketStatus = next
  emitSocketStatus()
}

function setState(next: WorkspaceState) {
  state = cloneState(next)
  emit()
}

function setWorkspaceHydrationStatus(next: WorkspaceHydrationStatus) {
  if (state.workspaceHydrationStatus === next) {
    return
  }

  state = cloneState({
    ...state,
    workspaceHydrationStatus: next,
  })
  emit()
}

function setWorkspaceSummaryHydrationStatus(next: WorkspaceSummaryHydrationStatus) {
  if (state.workspaceSummaryHydrationStatus === next) {
    return
  }

  state = cloneState({
    ...state,
    workspaceSummaryHydrationStatus: next,
  })
  emit()
}

type StartupBootstrapSnapshot = {
  people: Person[]
  astrologers: Astrologer[]
  conversations: StartupRouteInput["conversations"]
  credits: number
  latestConversationSummary: ConversationSummaryItem | null
}

type AstrologerCatalogItem = {
  astrologer_id: string
  slug: string
  display_name: string
  tagline: string | null
  style_summary: string | null
  avatar_url: string | null
  price_per_question_paise: number | null
  turn_ceiling_paise: number | null
  credit_multiplier: number
  rating_accuracy: number | null
  rating_speed: number | null
  rating_cost: number | null
  supports_matchmaking?: boolean
}

type AstrologerCatalogResponse = {
  items: AstrologerCatalogItem[]
}

type SubjectCatalogItem = {
  subject_id: string
  owner_user_id: string
  name: string
  relationship_to_owner: string
  is_default: boolean
  birth_name: string
  birth_datetime: string
  birth_lat: number
  birth_lng: number
  birth_place: string
  birth_timezone: string
  birth_time_source: string | null
  gender: string
  birth_time_lower: string
  birth_time_upper: string
  btr_verified: boolean
  birth_time_tier: number | null
  birth_time_status: string
  birth_time_confidence_label: string | null
  birth_time_uncertainty_minutes: number | null
  birth_time_window_mode: string | null
  chart_summary?: PersonChartSummary | null
  created_at: string
  updated_at: string
}

type SubjectCatalogResponse = {
  items: SubjectCatalogItem[]
}

function mapAstrologerCatalog(items: AstrologerCatalogItem[]): Astrologer[] {
  return items.map((astrologer) => ({
    id: astrologer.astrologer_id,
    slug: astrologer.slug,
    displayName: astrologer.display_name,
    tagline: astrologer.tagline ?? "",
    styleSummary: astrologer.style_summary ?? "",
    avatarUrl: astrologer.avatar_url,
    avatarColor: pickAstrologerColor(astrologer.slug),
    pricePerQuestionPaise: astrologer.price_per_question_paise,
    turnCeilingPaise: astrologer.turn_ceiling_paise,
    creditMultiplier: astrologer.credit_multiplier,
    ratingAccuracy: astrologer.rating_accuracy,
    ratingSpeed: astrologer.rating_speed,
    ratingCost: astrologer.rating_cost,
    supportsMatchmaking: Boolean(astrologer.supports_matchmaking),
  }))
}

function seedWorkspaceFromStartupSnapshot(snapshot: StartupBootstrapSnapshot) {
  if (!getAuthenticatedUserId()) {
    return
  }

  hydrate()
  const current = getWorkspaceState()
  const people = Array.isArray(snapshot.people) ? snapshot.people : []
  const astrologers = Array.isArray(snapshot.astrologers) ? snapshot.astrologers : current.astrologers
  const latestConversation = snapshot.latestConversationSummary
  const conversations = latestConversation
    ? mergeConversationSummaries(
        current.conversations,
        [
          {
            conversation_id: latestConversation.conversation_id,
            subject_id: latestConversation.subject_id,
            title: null,
            astrologer_id: latestConversation.astrologer_id,
            astrologer_slug: latestConversation.astrologer_slug,
            astrologer_display_name: latestConversation.astrologer_display_name,
            astrologer_avatar_url: latestConversation.astrologer_avatar_url,
            started_at: latestConversation.started_at,
            last_message_at: latestConversation.last_message_at,
            status: latestConversation.status,
          },
        ],
        getPlatformDefaultAstrologerId(astrologers)
      )
    : current.conversations
  const nextSelectedPersonId =
    people.find((person) => person.id === current.selectedPersonId)?.id ??
    (latestConversation && people.find((person) => person.id === latestConversation.subject_id)?.id) ??
    people.find((person) => person.isDefault)?.id ??
    people[0]?.id ??
    ""
  const nextActiveConversationId =
    latestConversation && conversations.some((conversation) => conversation.id === latestConversation.conversation_id)
      ? latestConversation.conversation_id
      : current.activeConversationId && conversations.some((conversation) => conversation.id === current.activeConversationId)
        ? current.activeConversationId
        : ""

  setState({
    ...current,
    people,
    astrologers,
    conversations,
    selectedPersonId: nextSelectedPersonId,
    activeConversationId: nextActiveConversationId,
    credits: snapshot.credits,
  })
}

export async function bootstrapStartupRouteData() {
  if (startupBootstrapInFlight) {
    return startupBootstrapInFlight
  }

  hydrate()
  const sessionVersion = workspaceSessionVersion
  const inFlight: Promise<StartupBootstrapSnapshot> = (async () => {
    const [subjectsResponse, astrologersResponse, creditsResponse, conversationsResponse] = await Promise.all([
      apiGet<{
        items: Array<{
          subject_id: string
          owner_user_id: string
          name: string
          relationship_to_owner: string
          is_default: boolean
          birth_name: string
          birth_datetime: string
          birth_lat: number
          birth_lng: number
          birth_place: string
          birth_timezone: string
          birth_time_source: string | null
          gender: string
          birth_time_lower: string
          birth_time_upper: string
          btr_verified: boolean
          birth_time_tier: number | null
          birth_time_status: string
          birth_time_confidence_label: string | null
          birth_time_uncertainty_minutes: number | null
          birth_time_window_mode: string | null
          created_at: string
          updated_at: string
        }>
      }>("/v1/subjects"),
      apiGet<AstrologerCatalogResponse>("/v1/astrologers", {
        cachePolicy: "stable-metadata",
      }),
      apiGet<CreditsResponse>("/v1/billing/credits"),
      apiGet<ConversationSummaryResponse>("/v1/conversations?limit=1"),
    ])

    const snapshot: StartupBootstrapSnapshot = {
      people: subjectsResponse.data.items.map(mapSubjectToPerson),
      astrologers: mapAstrologerCatalog(astrologersResponse.data.items),
      conversations: conversationsResponse.data.items.slice(0, 1).map((summary) => ({
        personId: summary.subject_id,
        astrologerId: summary.astrologer_id ?? "",
        updatedAt: summary.last_message_at,
      })),
      credits: creditsResponse.data.available_balance_paise,
      latestConversationSummary: conversationsResponse.data.items[0] ?? null,
    }

    if (workspaceSessionVersion !== sessionVersion) {
      return snapshot
    }

    seedWorkspaceFromStartupSnapshot(snapshot)
    return snapshot
  })()

  startupBootstrapInFlight = inFlight

  try {
    return await inFlight
  } finally {
    if (startupBootstrapInFlight === inFlight) {
      startupBootstrapInFlight = null
    }
  }
}

export async function refreshAstrologersCatalog() {
  hydrate()
  const currentUserId = getAuthenticatedUserId()
  if (!currentUserId) {
    return
  }

  const sessionVersion = workspaceSessionVersion

  try {
    const response = await apiGet<AstrologerCatalogResponse>("/v1/astrologers")
    if (workspaceSessionVersion !== sessionVersion || getAuthenticatedUserId() !== currentUserId) {
      return
    }

    const current = getWorkspaceState()
    setState({
      ...current,
      astrologers: mapAstrologerCatalog(response.data.items),
    })
  } catch {
    // Preserve the cached catalog if the refresh fails.
  }
}

export async function refreshWorkspacePeopleCatalog() {
  hydrate()
  const currentUserId = getAuthenticatedUserId()
  if (!currentUserId) {
    return false
  }

  const sessionVersion = workspaceSessionVersion

  try {
    const response = await apiGet<SubjectCatalogResponse>("/v1/subjects")
    if (workspaceSessionVersion !== sessionVersion || getAuthenticatedUserId() !== currentUserId) {
      return false
    }

    const people = mapSubjectCatalog(response.data.items)
    const current = getWorkspaceState()
    const currentSubjectIds = new Set(people.map((person) => person.id))
    const scopedConversations = current.conversations.filter((conversation) =>
      currentSubjectIds.has(conversation.personId)
    )
    const nextSelectedPersonId =
      people.some((person) => person.id === current.selectedPersonId)
        ? current.selectedPersonId
        : people.find((person) => person.isDefault)?.id ?? people[0]?.id ?? ""
    const nextActiveConversationId = scopedConversations.some(
      (conversation) => conversation.id === current.activeConversationId
    )
      ? current.activeConversationId
      : ""

    setState({
      ...current,
      people,
      conversations: scopedConversations,
      selectedPersonId: nextSelectedPersonId,
      activeConversationId: nextActiveConversationId,
    })
    return true
  } catch {
    return false
  }
}

export async function resolveStartupRoute() {
  const snapshot = await bootstrapStartupRouteData()
  return getStartupRoute(snapshot)
}

export function resolveCachedStartupRoute() {
  const current = getWorkspaceState()

  if (current.activeConversationId) {
    const activeConversation = current.conversations.find(
      (conversation) => conversation.id === current.activeConversationId
    )
    if (activeConversation) {
      return `/chat/${encodeURIComponent(activeConversation.id)}`
    }
  }

  if (current.conversations.length > 0) {
    const latestConversation = [...current.conversations].sort(
      (left, right) => right.updatedAt.localeCompare(left.updatedAt) || right.id.localeCompare(left.id)
    )[0]
    if (latestConversation) {
      return `/chat/${encodeURIComponent(latestConversation.id)}`
    }
  }

  if (current.people.length === 0 && current.astrologers.length === 0 && current.credits === 0) {
    return null
  }

  return getStartupRoute(current)
}

function assistantFeedbackMutationKey(conversationId: string, answerMessageId: string) {
  return `${conversationId}:${answerMessageId}`
}

function syncAssistantFeedbackConfirmedValuesFromConversation(conversation: Conversation) {
  const keyPrefix = `${conversation.id}:`
  const seen = new Set<string>()

  for (const turn of conversation.turns) {
    if (turn.status !== "complete" || typeof turn.answerMessageId !== "string") {
      continue
    }
    const key = assistantFeedbackMutationKey(conversation.id, turn.answerMessageId)
    seen.add(key)
    assistantFeedbackConfirmedValues.set(key, turn.assistantFeedback ?? null)
  }

  for (const key of [...assistantFeedbackConfirmedValues.keys()]) {
    if (key.startsWith(keyPrefix) && !seen.has(key)) {
      assistantFeedbackConfirmedValues.delete(key)
    }
  }
}

function syncAssistantFeedbackConfirmedValuesFromState(nextState: WorkspaceState) {
  assistantFeedbackConfirmedValues.clear()
  for (const conversation of nextState.conversations) {
    syncAssistantFeedbackConfirmedValuesFromConversation(conversation)
  }
}

function getAssistantFeedbackConfirmedValue(conversationId: string, answerMessageId: string) {
  return assistantFeedbackConfirmedValues.get(assistantFeedbackMutationKey(conversationId, answerMessageId)) ?? null
}

function setAssistantFeedbackConfirmedValue(
  conversationId: string,
  answerMessageId: string,
  value: "up" | "down" | null
) {
  assistantFeedbackConfirmedValues.set(assistantFeedbackMutationKey(conversationId, answerMessageId), value)
}

function clearAssistantFeedbackConfirmedValuesForConversation(conversationId: string) {
  const keyPrefix = `${conversationId}:`
  for (const key of [...assistantFeedbackConfirmedValues.keys()]) {
    if (key.startsWith(keyPrefix)) {
      assistantFeedbackConfirmedValues.delete(key)
    }
  }
}

function workspaceSocketUrl() {
  const url = new URL(apiBaseUrl())
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
  url.pathname = "/v1/ws"
  url.search = ""
  url.hash = ""
  return url.toString()
}

function getTurnRetryCount(turn: ConversationTurn) {
  return typeof turn.retryCount === "number" && Number.isFinite(turn.retryCount)
    ? turn.retryCount
    : 0
}

function buildFailedTurnState(turn: ConversationTurn, payload: Record<string, unknown>) {
  const retryable = typeof payload.retryable === "boolean" ? payload.retryable : false
  const retryCount = typeof payload.retry_count === "number" ? payload.retry_count : getTurnRetryCount(turn)
  const permanentFailure = retryable && retryCount >= MAX_RETRY_ATTEMPTS
  const details =
    payload.details && typeof payload.details === "object"
      ? (payload.details as Record<string, unknown>)
      : null
  const billing =
    payload.billing && typeof payload.billing === "object"
      ? (payload.billing as Record<string, unknown>)
      : null
  const availableBalancePaise =
    billing && typeof billing.available_balance_paise === "number"
      ? billing.available_balance_paise
      : details && typeof details.available_balance_paise === "number"
        ? details.available_balance_paise
        : turn.availableBalancePaise ?? null
  const amountDebitedPaise =
    billing && typeof billing.amount_debited_paise === "number"
      ? billing.amount_debited_paise
      : turn.amountDebitedPaise ?? null

  return {
    ...turn,
    status: "failed" as const,
    livePhase: undefined,
    errorCode: permanentFailure
      ? "permanent_failure"
      : typeof payload.code === "string"
        ? payload.code
        : "turn_failed",
    errorMessage: permanentFailure
      ? "This question has failed repeatedly. Please report it."
      : typeof payload.message === "string"
        ? payload.message
        : "The answer could not be completed.",
    errorDetails: details ?? undefined,
    retryable: permanentFailure ? false : retryable,
    retryCount,
    cancelRequested: false,
    availableBalancePaise,
    amountDebitedPaise,
    progressMessages: [],
    followUpState: undefined,
    followUpTurnId: undefined,
    followUpConversationId: undefined,
    followUpActionState: undefined,
  }
}

function resolveSourceFailedServerTurnId(
  conversation: Conversation,
  sourceFailedTurnId: string | undefined
) {
  if (!sourceFailedTurnId) {
    return undefined
  }

  const sourceTurn = conversation.turns.find(
    (turn) => turn.id === sourceFailedTurnId || turn.serverTurnId === sourceFailedTurnId
  )

  return sourceTurn?.serverTurnId
}

function failThinkingTurnsForConnectionLoss() {
  const current = getWorkspaceState()
  let updated = false

  const conversations = current.conversations.map((conversation) => {
    let conversationUpdated = false
    const turns: ConversationTurn[] = conversation.turns.map((turn) => {
      if (!isCancelableLiveTurn(turn)) {
        return turn
      }

      if (turn.cancelRequested) {
        // A disconnected socket cannot confirm settlement. Keep intent for attach/retry.
        return turn
      }
      conversationUpdated = true
      updated = true

      const retryable = true
      const hasServerAck = Boolean(turn.serverTurnId)
      const retryCount = hasServerAck
        ? getTurnRetryCount(turn)
        : Math.max(0, getTurnRetryCount(turn) - 1)
      const permanentFailure = hasServerAck && retryable && retryCount >= MAX_RETRY_ATTEMPTS
      return {
        ...turn,
        status: "failed" as const,
        errorCode: permanentFailure ? "permanent_failure" : "connection_lost",
        errorMessage: permanentFailure
          ? "This question has failed repeatedly. Please report it."
          : "Connection lost while waiting for the answer. The app will retry for a short time.",
        retryable: permanentFailure ? false : retryable,
        retryCount,
        cancelRequested: false,
        livePhase: undefined,
        progressMessages: [],
      }
    })

    if (!conversationUpdated) {
      return conversation
    }

    return {
      ...conversation,
      turns,
      updatedAt: nowLabel(),
    }
  })

  for (const [localTurnId, submit] of pendingSocketSubmits) {
    const turn = current.conversations.find((entry) => entry.id === submit.conversationId)
      ?.turns.find((entry) => entry.id === localTurnId)
    if (!turn?.cancelRequested || !isCancelableLiveTurn(turn)) {
      pendingSocketSubmits.delete(localTurnId)
    }
  }

  if (!updated) {
    return
  }

  setState({
    ...current,
    conversations,
  })
}

function findPendingSubmitByConversationId(conversationId: string) {
  for (const submit of pendingSocketSubmits.values()) {
    if (submit.conversationId === conversationId) {
      return submit
    }
  }
  return null
}

function updateConversationTurn(
  conversationId: string,
  matcher: (turn: ConversationTurn) => boolean,
  updater: (turn: ConversationTurn) => ConversationTurn,
  conversationPatch?: Partial<Conversation>,
  rootPatch?: Partial<WorkspaceState>
) {
  const current = getWorkspaceState()
  let updated = false
  const conversations = current.conversations.map((conversation) => {
    if (conversation.id !== conversationId) {
      return conversation
    }
    const index = conversation.turns.findIndex(matcher)
    if (index === -1) {
      return conversation
    }
    updated = true
    const turns = conversation.turns.map((turn, turnIndex) =>
      turnIndex === index ? updater(turn) : turn
    )
    return {
      ...conversation,
      ...conversationPatch,
      turns,
      updatedAt: conversationPatch?.updatedAt ?? nowLabel(),
    }
  })
  if (!updated) {
    return false
  }
  setState({
    ...current,
    ...rootPatch,
    conversations,
    activeConversationId: conversationId,
  })
  return true
}

function failUnsubmittedCancelledTurn(conversationId: string, localTurnId: string) {
  const current = getWorkspaceState()
  pendingSocketSubmits.delete(localTurnId)
  setState({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation
      }
      return {
        ...conversation,
        updatedAt: nowLabel(),
        turns: conversation.turns.map((turn) =>
          turn.id === localTurnId
            ? buildFailedTurnState(turn, {
                code: "cancelled_by_user",
                message: "Reading stopped.",
                retryable: false,
                retry_count: getTurnRetryCount(turn),
              })
            : turn
        ),
      }
    }),
  })
}

function failPendingTurnSubmission(conversationId: string, localTurnId: string) {
  pendingSocketSubmits.delete(localTurnId)
  updateConversationTurn(
    conversationId,
    (turn) => turn.id === localTurnId && !turn.serverTurnId,
    (turn) =>
      buildFailedTurnState(turn, {
        code: "connection_lost",
        message: "Could not submit the question because the chat connection was unavailable.",
        retryable: true,
        retry_count: Math.max(0, getTurnRetryCount(turn) - 1),
      })
  )
}

function sendTurnCancelIfReady(serverTurnId?: string) {
  if (!serverTurnId) {
    return false
  }
  return sendWorkspaceSocketEnvelope("message.cancel", { turn_id: serverTurnId })
}

function handleWorkspaceSocketMessage(envelope: SocketEnvelope) {
  const current = getWorkspaceState()
  const result = reduceSocketEvent(current, envelope)
  if (result.state !== current) setState(result.state)
  const payload = envelope.payload
  const conversationId = typeof payload.conversation_id === "string" ? payload.conversation_id : ""
  const submit = findPendingSubmitByConversationId(conversationId)
  if (submit && (envelope.event === "turn.accepted" || envelope.event === "turn.attached")) {
    pendingSocketSubmits.set(submit.localTurnId, { ...submit, serverTurnId: typeof payload.turn_id === "string" ? payload.turn_id : undefined })
  }
  if (envelope.event === "answer.final" || envelope.event === "turn.error") {
    for (const [id, pending] of pendingSocketSubmits) {
      if (pending.serverTurnId === payload.turn_id || pending.conversationId === conversationId) pendingSocketSubmits.delete(id)
    }
  }
  for (const effect of result.effects) {
    if (effect.type === "flushPendingCancel") flushPendingSocketCancels()
    if (effect.type === "resendCancelAfterAttach") flushPendingSocketCancels()
    if (effect.type === "sessionExpired") void expireAuthenticatedSession()
    if (effect.type === "consentRequired") showAiConsentDisclosure()
    // Web has no X-Device-Id and never acknowledges mobile push delivery.
  }
}

function getWorkspaceSocketManager() {
  if (workspaceSocketManager) return workspaceSocketManager
  workspaceSocketManager = createSocketManager({
    url: workspaceSocketUrl(),
    createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (handle) => window.clearTimeout(handle as number),
    now: () => Date.now(),
    getAuth: async () => ({}), // Web uses the existing HttpOnly session cookie.
    refreshSession: async () => {
      try { return await attemptRefreshingSession() ? "refreshed" : "definitive_failure" }
      catch { return "transient_failure" }
    },
    checkSession: async () => {
      try { await apiGet("/v1/auth/me"); return "valid" }
      catch (error) { return error instanceof ApiError && error.status === 401 ? "unauthorized" : "unknown" }
    },
    onEnvelope: handleWorkspaceSocketMessage,
    onStatus: (next) => {
      setWorkspaceSocketStatus(next)
      if (next === "failed") failThinkingTurnsForConnectionLoss()
      if (next === "open") { flushPendingSocketSubmits(); flushPendingSocketCancels() }
    },
    onSessionExpired: () => { void expireAuthenticatedSession() },
    log: (event, data) => {
      const { payload, ...metadata } = data ?? {}
      recordTracewayWebSocketEvent(event, metadata)
      const action = event === "open" ? "ws_open" : event === "error" ? "ws_error" : event === "close" ? "ws_close" : event === "connect attempt" ? "ws_connect" : null
      if (action) enqueueActivityEvent({ action, category: "websocket", metadata })
      if (event === "send" && payload && typeof payload === "object") {
        const wire = payload as Record<string, unknown>
        if (data?.event === "message.submit") enqueueActivityEvent({
          action: "ws_message_submit", category: "websocket",
          conversationId: typeof wire.conversation_id === "string" ? wire.conversation_id : null,
          metadata: { client_message_id: wire.client_message_id ?? null, retry_of_turn_id: wire.retry_of_turn_id ?? null, source_failed_turn_id: wire.source_failed_turn_id ?? null, local_turn_id: wire.client_message_id ?? null },
          submittedContent: typeof wire.content === "string" ? wire.content : null,
        })
        else if (data?.event === "turn.attach") enqueueActivityEvent({ action: "ws_message_submit", category: "websocket", metadata: { attach: true }, turnId: typeof wire.turn_id === "string" ? wire.turn_id : null })
      }
    },
  })
  return workspaceSocketManager
}

function sendWorkspaceSocketEnvelope(event: ClientEvent, payload: Record<string, unknown>) {
  return getWorkspaceSocketManager().send(event, payload)
}

function flushPendingSocketSubmits() {
  const manager = getWorkspaceSocketManager()
  for (const conversation of getWorkspaceState().conversations) {
    for (const turn of conversation.turns) {
      if (turn.status !== "thinking" && turn.status !== "streaming") continue
      if (turn.serverTurnId) { manager.attach(turn.serverTurnId); continue }
      if (!pendingSocketSubmits.has(turn.id)) pendingSocketSubmits.set(turn.id, {
        conversationId: conversation.id, content: turn.question, localTurnId: turn.id,
        retryOfTurnId: turn.retryOfTurnId, sourceFailedTurnId: turn.sourceFailedTurnId,
      })
    }
  }
  for (const pending of pendingSocketSubmits.values()) {
    if (pending.serverTurnId) manager.attach(pending.serverTurnId)
    else void manager.submit({ conversationId: pending.conversationId, content: pending.content, clientMessageId: pending.localTurnId, retryOfTurnId: pending.retryOfTurnId, sourceFailedTurnId: pending.sourceFailedTurnId }).catch(() => {
      failPendingTurnSubmission(pending.conversationId, pending.localTurnId)
    })
  }
}

function flushPendingSocketCancels() {
  for (const conversation of getWorkspaceState().conversations) {
    for (const turn of conversation.turns) {
      if (isCancelableLiveTurn(turn) && turn.cancelRequested) getWorkspaceSocketManager().cancel(conversation.id, turn.serverTurnId)
    }
  }
}

export function connectWorkspaceSocket() {
  if (typeof window === "undefined") return
  getWorkspaceSocketManager().connect()
}
export function pauseWorkspaceSocketForBackground() {
  workspaceSocketManager?.pauseForBackground()
}
export function resumeWorkspaceSocketFromBackground() {
  workspaceSocketManager?.resumeFromBackground()
}
export function disconnectWorkspaceSocket() {
  workspaceSocketManager?.disconnect()
}

export function resetWorkspaceState() {
  workspaceSessionVersion += 1
  remoteBootstrapInFlight = null
  startupBootstrapInFlight = null
  conversationSummaryRequests.clear()
  conversationMessageRequests.clear()
  assistantFeedbackMutationQueues.clear()
  assistantFeedbackMutationVersions.clear()
  assistantFeedbackConfirmedValues.clear()
  loadedConversationSummaryPersonIds.clear()
  hydrated = false
  hydratedForUserId = null
  pendingSocketSubmits.clear()
  disconnectWorkspaceSocket()
  state = cloneState(INITIAL_STATE)
  emitWithoutPersist()
}

async function submitTurnOverSocket(conversationId: string, localTurnId: string, question: string) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  const turn = conversation?.turns.find((entry) => entry.id === localTurnId)
  pendingSocketSubmits.set(localTurnId, {
    conversationId,
    content: question,
    localTurnId,
    serverTurnId: turn?.serverTurnId,
    retryOfTurnId: turn?.retryOfTurnId,
    sourceFailedTurnId: turn?.sourceFailedTurnId,
  })
  await getWorkspaceSocketManager().submit({ conversationId, content: question, clientMessageId: localTurnId, retryOfTurnId: turn?.retryOfTurnId, sourceFailedTurnId: turn?.sourceFailedTurnId })
}

export function getWorkspaceState() {
  hydrate()
  return state
}

export function subscribeWorkspace(listener: () => void) {
  hydrate()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useWorkspaceStore<T>(selector: (workspace: WorkspaceState) => T) {
  const snapshot = useSyncExternalStore(
    subscribeWorkspace,
    getWorkspaceState,
    () => INITIAL_STATE
  )
  return selector(snapshot)
}

export function getWorkspaceSocketStatus() {
  return workspaceSocketStatus
}

export function subscribeWorkspaceSocketStatus(listener: () => void) {
  socketStatusListeners.add(listener)
  return () => {
    socketStatusListeners.delete(listener)
  }
}

export function useWorkspaceSocketStatus() {
  return useSyncExternalStore(
    subscribeWorkspaceSocketStatus,
    getWorkspaceSocketStatus,
    (): SocketStatus => "closed"
  )
}

export async function bootstrapWorkspace() {
  if (remoteBootstrapInFlight) {
    return remoteBootstrapInFlight
  }

  hydrate()
  const currentUserId = getAuthenticatedUserId()
  if (state.workspaceHydrationStatus === "hydrated" && hydrated && hydratedForUserId === currentUserId) {
    return Promise.resolve()
  }

  const sessionVersion = workspaceSessionVersion
  setWorkspaceHydrationStatus("hydrating")
  const inFlight = (async () => {
    try {
      const [meResponse, subjectsResponse, astrologersResponse, creditsResponse] = await Promise.all([
        apiGet<{
          user_id: string
          email: string
          default_astrologer_id: string | null
          default_subject_id: string | null
          show_detailed_analysis: boolean
          features?: { matchmaking?: boolean }
        }>("/v1/users/me"),
        apiGet<SubjectCatalogResponse>("/v1/subjects"),
        apiGet<{
          items: Array<{
            astrologer_id: string
            slug: string
            display_name: string
            tagline: string | null
            style_summary: string | null
            avatar_url: string | null
            price_per_question_paise: number | null
            turn_ceiling_paise: number | null
            credit_multiplier: number
            rating_accuracy: number | null
            rating_speed: number | null
            rating_cost: number | null
            supports_matchmaking?: boolean
          }>
        }>("/v1/astrologers", {
          cachePolicy: "stable-metadata",
        }),
        apiGet<CreditsResponse>("/v1/billing/credits"),
      ])

      const people = mapSubjectCatalog(subjectsResponse.data.items)
      const astrologers = astrologersResponse.data.items.map((astrologer) => ({
        id: astrologer.astrologer_id,
        slug: astrologer.slug,
        displayName: astrologer.display_name,
        tagline: astrologer.tagline ?? "",
        styleSummary: astrologer.style_summary ?? "",
        avatarUrl: astrologer.avatar_url,
        avatarColor: pickAstrologerColor(astrologer.slug),
        pricePerQuestionPaise: astrologer.price_per_question_paise,
        turnCeilingPaise: astrologer.turn_ceiling_paise,
        creditMultiplier: astrologer.credit_multiplier,
        ratingAccuracy: astrologer.rating_accuracy,
        ratingSpeed: astrologer.rating_speed,
        ratingCost: astrologer.rating_cost,
        supportsMatchmaking: Boolean(astrologer.supports_matchmaking),
      }))
      const latest = getWorkspaceState()
      const currentSubjectIds = new Set(people.map((person) => person.id))
      const scopedConversations = latest.conversations.filter((conversation) =>
        currentSubjectIds.has(conversation.personId)
      )
      const nextSelectedPersonId =
        people.some((person) => person.id === latest.selectedPersonId)
          ? latest.selectedPersonId
          : meResponse.data.default_subject_id ||
            people.find((person) => person.isDefault)?.id ||
            people[0]?.id ||
            ""
      const nextActiveConversationId = scopedConversations.some(
        (conversation) => conversation.id === latest.activeConversationId
      )
        ? latest.activeConversationId
        : ""

      if (workspaceSessionVersion !== sessionVersion) {
        return
      }

      setState({
        ...latest,
        people,
        astrologers,
        conversations: scopedConversations,
        selectedPersonId: nextSelectedPersonId,
        activeConversationId: nextActiveConversationId,
        settings: {
          ...latest.settings,
          defaultAstrologerId: meResponse.data.default_astrologer_id ?? "",
          defaultSubjectId: meResponse.data.default_subject_id ?? "",
          showDetailedAnalysis: true,
          matchmakingEnabled: meResponse.data.features?.matchmaking === true,
        },
        credits: creditsResponse.data.available_balance_paise,
        workspaceHydrationStatus: "hydrated",
      })

      if (!nextSelectedPersonId || workspaceSessionVersion !== sessionVersion) {
        setWorkspaceSummaryHydrationStatus("ready")
        return
      }

      setWorkspaceSummaryHydrationStatus("warming")

      const selectedPersonWarmup = ensureConversationSummariesForPerson(nextSelectedPersonId, {
        limit: 10,
        force: true,
        propagateErrors: true,
      }).then(() => {
        if (workspaceSessionVersion === sessionVersion) {
          setWorkspaceSummaryHydrationStatus("ready")
        }
      })
      void selectedPersonWarmup.catch(() => {
        if (workspaceSessionVersion === sessionVersion) {
          setWorkspaceSummaryHydrationStatus("error")
        }
      })

      const backgroundPeople = people.filter((person) => person.id !== nextSelectedPersonId)
      void Promise.allSettled(
        backgroundPeople.map((person) => {
          if (workspaceSessionVersion !== sessionVersion) {
            return Promise.resolve(null)
          }
          return ensureConversationSummariesForPerson(person.id, {
            limit: 10,
            force: true,
            updateActiveConversation: false,
          })
        })
      )
    } catch {
      if (workspaceSessionVersion === sessionVersion) {
        setWorkspaceHydrationStatus("error")
        setWorkspaceSummaryHydrationStatus("error")
      }
    }
  })()
  remoteBootstrapInFlight = inFlight

  try {
    await inFlight
  } finally {
    if (remoteBootstrapInFlight === inFlight) {
      remoteBootstrapInFlight = null
    }
  }
}

export function getPerson(personId: string) {
  return getWorkspaceState().people.find((person) => person.id === personId) ?? null
}

export function getAstrologer(astrologerId: string) {
  return getWorkspaceState().astrologers.find((astrologer) => astrologer.id === astrologerId) ?? null
}

export function getConversation(conversationId: string) {
  return getWorkspaceState().conversations.find((conversation) => conversation.id === conversationId) ?? null
}

function removeConversationFromState(conversationId: string) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  if (!conversation) {
    return current.activeConversationId || null
  }

  for (const [localTurnId, submit] of pendingSocketSubmits.entries()) {
    if (submit.conversationId === conversationId) {
      pendingSocketSubmits.delete(localTurnId)
    }
  }
  clearAssistantFeedbackConfirmedValuesForConversation(conversationId)

  const remainingConversations = current.conversations.filter((entry) => entry.id !== conversationId)
  const personConversations = remainingConversations
    .filter((entry) => entry.personId === conversation.personId)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
  const fallbackConversation =
    personConversations[0] ?? [...remainingConversations].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null

  const nextActiveConversationId =
    current.activeConversationId === conversationId ? fallbackConversation?.id ?? "" : current.activeConversationId
  const nextSelectedPersonId =
    current.activeConversationId === conversationId && fallbackConversation
      ? fallbackConversation.personId
      : current.selectedPersonId

  setState({
    ...current,
    conversations: remainingConversations,
    activeConversationId: nextActiveConversationId,
    selectedPersonId: nextSelectedPersonId,
  })

  return nextActiveConversationId || null
}

export async function ensureConversationSummariesForPerson(
  personId: string,
  options?: {
    limit?: number
    force?: boolean
    updateActiveConversation?: boolean
    propagateErrors?: boolean
  }
) {
  const normalizedPersonId = personId.trim()
  if (!normalizedPersonId) {
    return null
  }

  if (!options?.force && loadedConversationSummaryPersonIds.has(normalizedPersonId)) {
    return getLatestConversationForPerson(normalizedPersonId)
  }

  const existingRequest = conversationSummaryRequests.get(normalizedPersonId)
  if (existingRequest) {
    return existingRequest
  }

  const sessionVersion = workspaceSessionVersion
  const request = (async () => {
    try {
      const response = await loadConversationSummariesPage(normalizedPersonId, {
        limit: options?.limit ?? 10,
        sessionVersion,
        updateStore: true,
        updateActiveConversation: options?.updateActiveConversation,
      })

      if (workspaceSessionVersion !== sessionVersion) {
        return null
      }
      loadedConversationSummaryPersonIds.add(normalizedPersonId)

      return getLatestConversationForPerson(normalizedPersonId)
    } catch (error) {
      if (options?.propagateErrors) {
        throw error
      }
      return null
    } finally {
      conversationSummaryRequests.delete(normalizedPersonId)
    }
  })()

  conversationSummaryRequests.set(normalizedPersonId, request)
  return request
}

export async function refreshConversationSummariesForAllPeople() {
  const current = getWorkspaceState()
  const people = current.people
  if (people.length === 0) {
    return false
  }

  const selectedPerson = people.find((person) => person.id === current.selectedPersonId) ?? null
  const remainingPeople = selectedPerson
    ? people.filter((person) => person.id !== selectedPerson.id)
    : people
  let refreshedAnyPerson = false

  if (selectedPerson) {
    try {
      await ensureConversationSummariesForPerson(selectedPerson.id, {
        force: true,
        updateActiveConversation: false,
        propagateErrors: true,
      })
      refreshedAnyPerson = true
    } catch {
      // Continue refreshing the remaining people.
    }
  }

  const remainingResults = await Promise.allSettled(
    remainingPeople.map((person) =>
      ensureConversationSummariesForPerson(person.id, {
        force: true,
        updateActiveConversation: false,
        propagateErrors: true,
      })
    )
  )
  if (remainingResults.some((result) => result.status === "fulfilled")) {
    refreshedAnyPerson = true
  }

  return refreshedAnyPerson
}

export function getLatestConversationForPerson(personId: string) {
  const current = getWorkspaceState()
  return (
    current.conversations
      .filter((conversation) => conversation.personId === personId)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null
  )
}

export function getLatestConversation() {
  const current = getWorkspaceState()
  return (
    [...current.conversations].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null
  )
}

export function getActiveConversation() {
  const current = getWorkspaceState()
  return current.conversations.find((conversation) => conversation.id === current.activeConversationId) ?? current.conversations[0] ?? null
}

export function selectPerson(personId: string) {
  const current = getWorkspaceState()
  if (!current.people.some((person) => person.id === personId)) {
    return
  }
  if (current.selectedPersonId === personId) {
    return
  }

  setState({
    ...current,
    selectedPersonId: personId,
  })
}

export function setActiveConversation(conversationId: string) {
  const current = getWorkspaceState()
  if (!current.conversations.some((conversation) => conversation.id === conversationId)) {
    return
  }
  if (current.activeConversationId === conversationId) {
    return
  }

  setState({
    ...current,
    activeConversationId: conversationId,
  })
}

export async function deleteConversation(conversationId: string) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  await apiDelete(`/v1/conversations/${conversationId}`)

  if (!conversation) {
    return current.activeConversationId || null
  }

  return removeConversationFromState(conversationId)
}

/**
 * Build an ISO 8601 datetime string that includes the birth timezone offset,
 * so the backend can correctly convert to UTC when storing.
 *
 * e.g. "1994-08-14T10:30:00+05:30" for IST — not "10:30:00Z" which would
 * be 5h30m off.
 */
function computeBirthUtcOffset(date: string, time: string, timezone: string): string {
  return computeUtcOffsetForTimezone(date, time, timezone)
}

function toBirthDatetimeIso(date: string, time: string, timezone: string): string {
  const localIso = `${date}T${time || '00:00'}:00`
  const offset = computeBirthUtcOffset(date, time, timezone)
  return `${localIso}${offset}`
}

function addDaysToDate(date: string, days: number): string {
  const current = new Date(`${date}T00:00:00Z`)
  current.setUTCDate(current.getUTCDate() + days)
  return current.toISOString().slice(0, 10)
}

function shiftIsoByMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString()
}

function isoToLocalDateTimeParts(iso: string, timezone: string): { date: string; time: string } {
  const dt = new Date(iso)
  const dateParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(dt)
  const timeParts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(dt)
  const year = dateParts.find((part) => part.type === "year")?.value ?? "0000"
  const month = dateParts.find((part) => part.type === "month")?.value ?? "01"
  const day = dateParts.find((part) => part.type === "day")?.value ?? "01"
  const hour = timeParts.find((part) => part.type === "hour")?.value ?? "00"
  const minute = timeParts.find((part) => part.type === "minute")?.value ?? "00"
  return {
    date: `${year}-${month}-${day}`,
    time: `${hour.padStart(2, "0")}:${minute}`,
  }
}

function midpointIso(startIso: string, endIso: string): string {
  const startMs = new Date(startIso).getTime()
  const endMs = new Date(endIso).getTime()
  return new Date((startMs + endMs) / 2).toISOString()
}

function getBirthTimeWindowMinutes(tier: number | null): number {
  switch (tier) {
    case 0:
      return 0
    case 1:
      return 2
    case 2:
      return 5
    case 3:
      return 15
    case 4:
      return 60
    default:
      return 60
  }
}

function computeBirthTimeWindowIsoBounds(input: PersonInput): { start: string; end: string } {
  const birthDatetimeIso = toBirthDatetimeIso(input.birthDate, input.birthTime || "00:00", input.birthTimezone)

  if (input.birthTimeTier === null) {
    const start = toBirthDatetimeIso(input.birthDate, "00:00", input.birthTimezone)
    const end = toBirthDatetimeIso(addDaysToDate(input.birthDate, 1), "00:00", input.birthTimezone)
    return { start, end }
  }

  if (input.birthTimeTier === -1) {
    if (!input.birthTimeWindowFrom || !input.birthTimeWindowTo) {
      throw new Error("Custom birth time range requires both start and end times.")
    }
    const endDate = input.birthTimeWindowTo < input.birthTimeWindowFrom
      ? addDaysToDate(input.birthDate, 1)
      : input.birthDate
    return {
      start: toBirthDatetimeIso(input.birthDate, input.birthTimeWindowFrom, input.birthTimezone),
      end: toBirthDatetimeIso(endDate, input.birthTimeWindowTo, input.birthTimezone),
    }
  }

  const widthMinutes = getBirthTimeWindowMinutes(input.birthTimeTier)
  return {
    start: shiftIsoByMinutes(birthDatetimeIso, -widthMinutes),
    end: shiftIsoByMinutes(birthDatetimeIso, widthMinutes),
  }
}

function deriveBirthTimeTierFromWindow(startIso: string | null | undefined, endIso: string | null | undefined, btrVerified: boolean) {
  if (btrVerified) {
    return 0
  }
  if (!startIso || !endIso) {
    return null
  }
  const widthMinutes = Math.max(0, Math.round(Math.abs(new Date(endIso).getTime() - new Date(startIso).getTime()) / 120_000))
  if (widthMinutes <= 0) {
    return 0
  }
  if (widthMinutes <= 2) {
    return 1
  }
  if (widthMinutes <= 5) {
    return 2
  }
  if (widthMinutes <= 15) {
    return 3
  }
  return 4
}

function matchesCanonicalTierWindow(
  birthDate: string,
  birthTime: string,
  birthTimezone: string,
  tier: number,
  startIso: string,
  endIso: string
) {
  const widthMinutes = getBirthTimeWindowMinutes(tier)
  const centerIso = toBirthDatetimeIso(birthDate, birthTime || "00:00", birthTimezone)
  const expectedStart = shiftIsoByMinutes(centerIso, -widthMinutes)
  const expectedEnd = shiftIsoByMinutes(centerIso, widthMinutes)
  return (
    new Date(expectedStart).getTime() === new Date(startIso).getTime() &&
    new Date(expectedEnd).getTime() === new Date(endIso).getTime()
  )
}

function buildPersistedBirthDatetime(input: PersonInput, birthWindow: { start: string; end: string }) {
  if (input.birthTimeTier === -1) {
    const midpoint = midpointIso(birthWindow.start, birthWindow.end)
    return isoToLocalDateTimeParts(midpoint, input.birthTimezone)
  }
  return {
    date: input.birthDate,
    time: input.birthTime || "00:00",
  }
}

function toBackendGender(gender: string): string {
  if (gender === 'F' || gender.toLowerCase() === 'female') return 'F'
  return 'M' // Male and Other both map to 'M'
}

type SubjectApiRecord = {
  subject_id: string
  owner_user_id: string
  name: string
  relationship_to_owner: string
  is_default: boolean
  birth_name: string
  birth_datetime: string
  birth_lat: number
  birth_lng: number
  birth_place: string
  birth_timezone: string
  birth_utc_offset: string | null
  birth_time_source: string | null
  gender: string
  birth_time_lower: string
  birth_time_upper: string
  btr_verified: boolean
  birth_time_tier: number | null
  birth_time_status: string
  birth_time_confidence_label: string | null
  birth_time_uncertainty_minutes: number | null
  birth_time_window_mode: string | null
  chart_summary?: PersonChartSummary | null
  created_at: string
  updated_at: string
}

export async function createPerson(input: PersonInput) {
  if (input.birthLat === null || input.birthLng === null) {
    throw new Error("Birth place coordinates are required. Select a place from the suggestions before saving.")
  }

  const birthWindow = computeBirthTimeWindowIsoBounds(input)
  const persistedBirth = buildPersistedBirthDatetime(input, birthWindow)

  const response = await apiMutate<SubjectApiRecord>("/v1/subjects", {
    body: JSON.stringify({
      name: input.name,
      relationship_to_owner: input.relationshipToOwner || "other",
      birth_name: input.birthName || input.name,
      birth_datetime: toBirthDatetimeIso(persistedBirth.date, persistedBirth.time, input.birthTimezone),
      birth_utc_offset: input.birthUtcOffset.trim() || computeBirthUtcOffset(persistedBirth.date, persistedBirth.time, input.birthTimezone),
      birth_lat: input.birthLat,
      birth_lng: input.birthLng,
      birth_place: input.birthPlace,
      birth_timezone: input.birthTimezone,
      birth_time_source: input.birthTimeSource || null,
      gender: toBackendGender(input.gender),
      birth_time_lower: birthWindow.start,
      birth_time_upper: birthWindow.end,
    }),
    idempotencyKey: nanoid(),
    method: "POST",
  })

  const nextPerson = mapSubjectToPerson(response.data)
  enqueueActivityEvent({
    action: "person_create_submit",
    category: "people",
    metadata: {
      birth_place: input.birthPlace,
      birth_time_source: input.birthTimeSource || null,
      relationship_to_owner: input.relationshipToOwner || "other",
    },
    subjectId: nextPerson.id,
    submittedContent: JSON.stringify({
      birth_date: input.birthDate,
      birth_name: input.birthName || input.name,
      birth_place: input.birthPlace,
      birth_time: input.birthTime,
      birth_timezone: input.birthTimezone,
      gender: input.gender,
      name: input.name,
      relationship_to_owner: input.relationshipToOwner || "other",
    }),
  })
  const current = getWorkspaceState()
  setState({
    ...current,
    people: [nextPerson, ...current.people.filter((person) => person.id !== nextPerson.id)],
    selectedPersonId: nextPerson.id,
  })

  return nextPerson
}

export async function updatePerson(personId: string, input: PersonInput) {
  const hasBirthCoordinates = input.birthLat !== null && input.birthLng !== null
  const birthWindow = computeBirthTimeWindowIsoBounds(input)
  const persistedBirth = buildPersistedBirthDatetime(input, birthWindow)

  const response = await apiMutate<SubjectApiRecord>(`/v1/subjects/${personId}`, {
    body: JSON.stringify({
      name: input.name,
      relationship_to_owner: input.relationshipToOwner || "other",
      birth_name: input.birthName || input.name,
      birth_datetime: toBirthDatetimeIso(persistedBirth.date, persistedBirth.time, input.birthTimezone),
      birth_utc_offset: input.birthUtcOffset.trim() || computeBirthUtcOffset(persistedBirth.date, persistedBirth.time, input.birthTimezone),
      ...(hasBirthCoordinates ? { birth_lat: input.birthLat, birth_lng: input.birthLng } : {}),
      birth_place: input.birthPlace,
      birth_timezone: input.birthTimezone,
      birth_time_source: input.birthTimeSource || null,
      gender: toBackendGender(input.gender),
      birth_time_lower: birthWindow.start,
      birth_time_upper: birthWindow.end,
    }),
    idempotencyKey: nanoid(),
    method: "PATCH",
  })

  const nextPerson = mapSubjectToPerson(response.data)
  enqueueActivityEvent({
    action: "person_edit_submit",
    category: "people",
    metadata: {
      birth_place: input.birthPlace,
      birth_time_source: input.birthTimeSource || null,
      relationship_to_owner: input.relationshipToOwner || "other",
    },
    subjectId: nextPerson.id,
    submittedContent: JSON.stringify({
      birth_date: input.birthDate,
      birth_name: input.birthName || input.name,
      birth_place: input.birthPlace,
      birth_time: input.birthTime,
      birth_timezone: input.birthTimezone,
      gender: input.gender,
      name: input.name,
      relationship_to_owner: input.relationshipToOwner || "other",
    }),
  })
  const current = getWorkspaceState()
  setState({
    ...current,
    people: current.people.map((person) => (person.id === personId ? nextPerson : person)),
  })

  return nextPerson
}

export async function setDefaultAstrologer(astrologerId: string) {
  await apiMutate("/v1/user/default-astrologer", {
    body: JSON.stringify({ astrologer_id: astrologerId }),
    idempotencyKey: nanoid(),
    method: "PUT",
  })

  const current = getWorkspaceState()
  const astrologer = current.astrologers.find((entry) => entry.id === astrologerId)
  enqueueActivityEvent({
    action: "astrologer_select",
    category: "astrologer",
    metadata: {
      astrologer_display_name: astrologer?.displayName ?? null,
      previous_astrologer_id: current.settings.defaultAstrologerId || null,
    },
  })
  setState({
    ...current,
    settings: {
      ...current.settings,
      defaultAstrologerId: astrologerId,
    },
  })
}

export async function setDefaultSubject(subjectId: string) {
  await apiMutate("/v1/users/me", {
    body: JSON.stringify({ default_subject_id: subjectId || null }),
    idempotencyKey: nanoid(),
    method: "PATCH",
  })

  const current = getWorkspaceState()
  const hasSelectedPerson = current.people.some((person) => person.id === current.selectedPersonId)
  const shouldFollowDefault =
    !current.selectedPersonId ||
    current.selectedPersonId === current.settings.defaultSubjectId ||
    !hasSelectedPerson
  const nextSelectedPersonId = shouldFollowDefault ? subjectId : current.selectedPersonId

  setState({
    ...current,
    settings: {
      ...current.settings,
      defaultSubjectId: subjectId,
    },
    selectedPersonId: nextSelectedPersonId,
  })
}

export async function createConversation(input: ConversationInput) {
  const current = getWorkspaceState()
  const person = current.people.find((entry) => entry.id === input.personId) ?? current.people[0]
  if (!person) {
    throw new Error("No person is available to start a conversation")
  }

  const astrologerId =
    input.astrologerId ??
    current.settings.defaultAstrologerId ??
    getPlatformDefaultAstrologerId(current.astrologers) ??
    ""
  if (!astrologerId) {
    throw new Error("No astrologer is available to start a conversation")
  }
  const prompt = input.openingPrompt?.trim() ?? ""
  if (prompt && !await requireAiConsent()) throw new ConsentCancelledError()

  const response = await apiMutate<{
    conversation_id: string
    subject_id: string
    astrologer_id: string
    astrologer_slug: string
    astrologer_display_name: string
    astrologer_avatar_url: string | null
    source_failed_turn_id: string | null
    started_at: string
    last_message_at: string
    title: string | null
  }>("/v1/conversations", {
    body: JSON.stringify({
      subject_id: person.id,
      astrologer_id: astrologerId,
      title: null,
      initial_message: prompt || null,
      source_failed_turn_id: input.sourceFailedTurnId ?? null,
      client_context: { source: "frontend" },
    }),
    idempotencyKey: nanoid(),
    method: "POST",
  })

  const astrologer =
    current.astrologers.find((entry) => entry.id === response.data.astrologer_id) ??
    getPlatformDefaultAstrologer(current.astrologers)
  const nextConversation: Conversation = {
    id: response.data.conversation_id,
    personId: response.data.subject_id,
    astrologerId: response.data.astrologer_id,
    astrologerDisplayName: response.data.astrologer_display_name,
    astrologerAvatarUrl: response.data.astrologer_avatar_url,
    astrologerSlug: response.data.astrologer_slug,
    sourceFailedTurnId: response.data.source_failed_turn_id ?? undefined,
    title: response.data.title ?? "New conversation",
    summary: prompt || `${person.name} • ${astrologer?.displayName ?? response.data.astrologer_display_name}`,
    updatedAt: response.data.last_message_at,
    historyLoadStatus: "loaded",
    composerDraft: "",
    composerSourceFailedTurnId: undefined,
    turns: [],
  }

  setState({
    ...current,
    conversations: [nextConversation, ...current.conversations.filter((conversation) => conversation.id !== nextConversation.id)],
    activeConversationId: nextConversation.id,
    selectedPersonId: person.id,
  })
  enqueueActivityEvent({
    action: "conversation_create",
    category: "conversation",
    conversationId: nextConversation.id,
    metadata: {
      astrologer_id: nextConversation.astrologerId,
      opening_prompt_present: Boolean(prompt),
      source_failed_turn_id: input.sourceFailedTurnId ?? null,
    },
    subjectId: person.id,
    submittedContent: prompt || null,
  })

  if (prompt) {
    const turnId = startConversationTurn(nextConversation.id, prompt)
    if (turnId) {
      void finishConversationTurn(nextConversation.id, turnId, prompt)
    }
  }

  return nextConversation
}

export type MatchConversationInput = {
  matchId: string
  primaryPersonId: string
  astrologerId: string
}

export type MatchConversationResult = {
  conversation: Conversation
  /** Set when the server could not start the report turn (e.g. low balance). */
  reportError: { code: string; message: string } | null
}

/**
 * Create a match chat whose first turn is the server-owned compatibility
 * report (MATCHMAKING_LLD §11.2, §8.3). The server admits the report turn,
 * so the client only mirrors it as a live turn and listens on the socket.
 */
export async function createMatchConversation(
  input: MatchConversationInput
): Promise<MatchConversationResult> {
  if (!await requireAiConsent()) throw new ConsentCancelledError()
  const current = getWorkspaceState()
  const response = await apiMutate<{
    conversation_id: string
    subject_id: string
    astrologer_id: string
    astrologer_slug: string
    astrologer_display_name: string
    astrologer_avatar_url: string | null
    started_at: string
    last_message_at: string
    title: string | null
    match_id: string | null
    partner_subject_id: string | null
    partner_name: string | null
    report_request_text: string | null
    report_turn: { turn_id: string; message_id: string; status: string } | null
    report_turn_error: { code: string; message: string } | null
  }>("/v1/conversations", {
    body: JSON.stringify({
      subject_id: input.primaryPersonId,
      astrologer_id: input.astrologerId,
      title: null,
      match_id: input.matchId,
      start_with_report: true,
      client_context: { source: "frontend", entry: "match_report" },
    }),
    idempotencyKey: nanoid(),
    method: "POST",
  })
  const data = response.data
  const reportText = data.report_request_text ?? ""
  const createdAt = nowLabel()
  const turns: ConversationTurn[] = data.report_turn
    ? [
        {
          id: nanoid(),
          question: reportText,
          answer: "",
          detailedAnswer: "",
          reasoning: "",
          sources: [],
          suggestions: [],
          status: "thinking",
          livePhase: "thinking",
          serverTurnId: data.report_turn.turn_id,
          requestMessageId: data.report_turn.message_id,
          progressMessages: ["Loading both charts"],
          cancelRequested: false,
          retryCount: 0,
          amountDebitedPaise: null,
          availableBalancePaise: null,
          createdAt,
        },
      ]
    : []
  const conversation: Conversation = {
    id: data.conversation_id,
    personId: data.subject_id,
    astrologerId: data.astrologer_id,
    astrologerDisplayName: data.astrologer_display_name,
    astrologerAvatarUrl: data.astrologer_avatar_url,
    astrologerSlug: data.astrologer_slug,
    matchId: data.match_id,
    partnerSubjectId: data.partner_subject_id,
    partnerName: data.partner_name,
    title: data.title ?? "Compatibility",
    summary: reportText,
    updatedAt: data.last_message_at,
    historyLoadStatus: "loaded",
    // Without a report turn the user can send the request themselves.
    composerDraft: data.report_turn ? "" : reportText,
    composerSourceFailedTurnId: undefined,
    turns,
  }

  setState({
    ...current,
    conversations: [conversation, ...current.conversations.filter((entry) => entry.id !== conversation.id)],
    activeConversationId: conversation.id,
    selectedPersonId: data.subject_id,
  })
  enqueueActivityEvent({
    action: "conversation_create",
    category: "conversation",
    conversationId: conversation.id,
    metadata: {
      astrologer_id: conversation.astrologerId,
      match_id: data.match_id,
      report_turn_started: Boolean(data.report_turn),
      report_turn_error: data.report_turn_error?.code ?? null,
    },
    subjectId: data.subject_id,
    submittedContent: null,
  })
  if (data.report_turn) {
    connectWorkspaceSocket()
  }
  return { conversation, reportError: data.report_turn_error }
}

export function startConversationTurn(
  conversationId: string,
  question: string,
  options?: { retryCount?: number; retryOfTurnId?: string; sourceFailedTurnId?: string }
) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  if (!conversation) {
    return null
  }

  const person = current.people.find((entry) => entry.id === conversation.personId) ?? current.people[0]
  const astrologer =
    current.astrologers.find((entry) => entry.id === conversation.astrologerId) ??
    getPlatformDefaultAstrologer(current.astrologers)
  const createdAt = nowLabel()
  const sourceFailedServerTurnId = resolveSourceFailedServerTurnId(
    conversation,
    options?.sourceFailedTurnId
  )
  const turn: ConversationTurn = {
    id: nanoid(),
    question: question.trim(),
    answer: "",
    detailedAnswer: "",
    reasoning: "",
    sources: [],
    suggestions: buildSuggestions(question),
    status: "thinking",
    livePhase: "thinking",
    progressMessages: ["Loading charts"],
    cancelRequested: false,
    retryOfTurnId: options?.retryOfTurnId,
    sourceFailedTurnId: sourceFailedServerTurnId,
    retryCount: options?.retryCount ?? 0,
    amountDebitedPaise: null,
    availableBalancePaise: null,
    createdAt,
  }

  setState({
    ...current,
    activeConversationId: conversationId,
    conversations: current.conversations.map((entry) => {
      if (entry.id !== conversationId) {
        return entry
      }
      return {
        ...entry,
        updatedAt: createdAt,
        summary: turn.question,
        composerDraft: "",
        composerSourceFailedTurnId: undefined,
        turns: [...entry.turns, turn],
      }
    }),
  })

  return turn.id
}

export async function finishConversationTurn(conversationId: string, turnId: string, question: string) {
  const conversation = getWorkspaceState().conversations.find((entry) => entry.id === conversationId)
  if (!conversation) {
    return
  }
  const turn = conversation.turns.find((entry) => entry.id === turnId)
  if (turn?.cancelRequested && !turn.serverTurnId) {
    failUnsubmittedCancelledTurn(conversationId, turnId)
    return
  }

  try {
    await submitTurnOverSocket(conversationId, turnId, question)
  } catch (error) {
    if (error instanceof Error && error.message === SESSION_EXPIRED_ERROR_MESSAGE) {
      await expireAuthenticatedSession()
      return
    }
    failPendingTurnSubmission(conversationId, turnId)
  }
}

export function requestConversationTurnCancel(conversationId: string) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  if (!conversation) {
    return false
  }

  const pendingTurn = [...conversation.turns]
    .reverse()
    .find((turn) => isCancelableLiveTurn(turn))
  if (!pendingTurn || pendingTurn.cancelRequested) {
    return false
  }

  const pendingSubmit = pendingSocketSubmits.get(pendingTurn.id)
  const serverTurnId = pendingTurn.serverTurnId ?? pendingSubmit?.serverTurnId
  enqueueActivityEvent({
    action: "chat_cancel",
    category: "chat",
    conversationId,
    metadata: {
      local_turn_id: pendingTurn.id,
      server_turn_id: serverTurnId ?? null,
    },
    turnId: serverTurnId ?? null,
  })
  setState({
    ...current,
    conversations: current.conversations.map((entry) => {
      if (entry.id !== conversationId) {
        return entry
      }
      return {
        ...entry,
        updatedAt: nowLabel(),
        turns: entry.turns.map((turn) =>
          turn.id === pendingTurn.id
            ? {
                ...turn,
                cancelRequested: true,
                progressMessages:
                  turn.progressMessages.at(-1) === "Stopping…"
                    ? turn.progressMessages
                    : [...turn.progressMessages, "Stopping…"],
              }
            : turn
        ),
      }
    }),
  })

  connectWorkspaceSocket()
  if (pendingSubmit && !serverTurnId) {
    flushPendingSocketSubmits()
  }
  if (serverTurnId && !sendTurnCancelIfReady(serverTurnId)) {
    flushPendingSocketCancels()
  }
  return true
}

export function updateConversationAstrologer(conversationId: string, astrologerId: string) {
  const current = getWorkspaceState()
  const astrologer = current.astrologers.find((entry) => entry.id === astrologerId)
  if (!astrologer) {
    return
  }

  setState({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation
      }
      return {
        ...conversation,
        astrologerId,
        updatedAt: nowLabel(),
      }
    }),
  })
}

export function setConversationComposerDraft(conversationId: string, draft: string) {
  const current = getWorkspaceState()
  setState({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation
      }
      return {
        ...conversation,
        composerDraft: draft,
      }
    }),
  })
}

export function primeConversationComposerFromFailedTurn(conversationId: string, failedTurnId: string, draft: string) {
  const current = getWorkspaceState()
  setState({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation
      }
      return {
        ...conversation,
        composerDraft: draft,
        composerSourceFailedTurnId: failedTurnId,
      }
    }),
  })
}

export function markFailedTurnAction(
  conversationId: string,
  failedTurnId: string,
  followUpActionState: NonNullable<ConversationTurn["followUpActionState"]>
) {
  const current = getWorkspaceState()
  setState({
    ...current,
    conversations: current.conversations.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation
      }
      return {
        ...conversation,
        turns: conversation.turns.map((turn) =>
          turn.id === failedTurnId &&
          turn.status === "failed" &&
          !turn.followUpState &&
          !turn.followUpActionState
            ? {
                ...turn,
                followUpActionState,
              }
            : turn
        ),
      }
    }),
  })
}

export function getSelectedPerson() {
  const current = getWorkspaceState()
  return current.people.find((person) => person.id === current.selectedPersonId) ?? current.people[0] ?? null
}

export function getDefaultAstrologer() {
  const current = getWorkspaceState()
  return (
    current.astrologers.find((astrologer) => astrologer.id === current.settings.defaultAstrologerId) ??
    getPlatformDefaultAstrologer(current.astrologers)
  )
}

export function setWorkspaceCredits(credits: number) {
  const current = getWorkspaceState()
  if (current.credits === credits) {
    return
  }

  setState({
    ...current,
    credits,
  })
}

export async function setAssistantFeedback(
  conversationId: string,
  answerMessageId: string,
  value: "up" | "down" | null
) {
  const normalizedConversationId = conversationId.trim()
  const normalizedAnswerMessageId = answerMessageId.trim()
  if (!normalizedConversationId || !normalizedAnswerMessageId) {
    return null
  }

  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === normalizedConversationId)
  const turn = conversation?.turns.find((entry) => entry.answerMessageId === normalizedAnswerMessageId)
  if (!conversation || !turn || turn.status !== "complete") {
    return null
  }

  const currentFeedback = turn.assistantFeedback ?? null
  if (currentFeedback === value) {
    return currentFeedback
  }

  const confirmedFeedback = getAssistantFeedbackConfirmedValue(
    normalizedConversationId,
    normalizedAnswerMessageId
  )

  const mutationKey = assistantFeedbackMutationKey(normalizedConversationId, normalizedAnswerMessageId)
  const nextVersion = (assistantFeedbackMutationVersions.get(mutationKey) ?? 0) + 1
  assistantFeedbackMutationVersions.set(mutationKey, nextVersion)

  updateConversationTurn(
    normalizedConversationId,
    (entry) => entry.answerMessageId === normalizedAnswerMessageId && entry.status === "complete",
    (entry) => ({
      ...entry,
      assistantFeedback: value,
    })
  )

  const priorRequest = assistantFeedbackMutationQueues.get(mutationKey) ?? Promise.resolve()
  const request = priorRequest
    .catch(() => undefined)
    .then(async () => {
      const response = await apiMutate<{
        logged: true
        assistant_feedback: "up" | "down" | null
      }>(`/v1/conversations/${normalizedConversationId}/messages/${normalizedAnswerMessageId}/feedback`, {
        body: JSON.stringify({ value }),
        idempotencyKey: nanoid(),
        method: "PUT",
      })
      const serverFeedback = response.data.assistant_feedback ?? null
      setAssistantFeedbackConfirmedValue(normalizedConversationId, normalizedAnswerMessageId, serverFeedback)
      if (serverFeedback !== value) {
        updateConversationTurn(
          normalizedConversationId,
          (entry) => entry.answerMessageId === normalizedAnswerMessageId && entry.status === "complete",
          (entry) => ({
            ...entry,
            assistantFeedback: serverFeedback,
          })
        )
      }
      return serverFeedback
    })
    .catch((error) => {
      if (assistantFeedbackMutationVersions.get(mutationKey) === nextVersion) {
        updateConversationTurn(
          normalizedConversationId,
          (entry) => entry.answerMessageId === normalizedAnswerMessageId && entry.status === "complete",
          (entry) => ({
            ...entry,
            assistantFeedback: getAssistantFeedbackConfirmedValue(
              normalizedConversationId,
              normalizedAnswerMessageId
            ),
          })
        )
      }
      throw error
    })
    .finally(() => {
      if (assistantFeedbackMutationQueues.get(mutationKey) === request) {
        assistantFeedbackMutationQueues.delete(mutationKey)
      }
    })

  assistantFeedbackMutationQueues.set(mutationKey, request)
  return request
}

export async function logAssistantInteraction(
  conversationId: string,
  answerMessageId: string,
  action: "copy" | "share"
) {
  const normalizedConversationId = conversationId.trim()
  const normalizedAnswerMessageId = answerMessageId.trim()
  if (!normalizedConversationId || !normalizedAnswerMessageId) {
    return
  }

  try {
    await apiMutate<{ logged: true }>(
      `/v1/conversations/${normalizedConversationId}/messages/${normalizedAnswerMessageId}/interaction`,
      {
        body: JSON.stringify({ action }),
        idempotencyKey: nanoid(),
        method: "POST",
      }
    )
  } catch {
    // Fire-and-forget logging intentionally swallows failures.
  }
}

export async function refreshConversationMessages(
  conversationId: string,
  options?: { preserveActiveSelection?: boolean; sessionVersion?: number }
) {
  const normalizedConversationId = conversationId.trim()
  if (!normalizedConversationId) {
    return
  }

  const sessionVersion = options?.sessionVersion ?? workspaceSessionVersion
  const existingRequest = conversationMessageRequests.get(normalizedConversationId)
  if (existingRequest) {
    return existingRequest
  }

  const currentBeforeRequest = getWorkspaceState()
  const existingBeforeRequest =
    currentBeforeRequest.conversations.find((conversation) => conversation.id === normalizedConversationId) ?? null
  if (existingBeforeRequest && existingBeforeRequest.historyLoadStatus !== "loading") {
    setState({
      ...currentBeforeRequest,
      conversations: currentBeforeRequest.conversations.map((conversation) =>
        conversation.id === normalizedConversationId
          ? { ...conversation, historyLoadStatus: "loading" }
          : conversation
      ),
    })
  }

  const request = (async () => {
    try {
      const items: ConversationMessageItem[] = []
      const turnsByRequestMessageId = new Map<string, ConversationMessageTurn>()
      let cursor: string | null = null
      let lastResponse: ConversationMessagesResponse | null = null

      while (true) {
        const query = new URLSearchParams({ limit: "100" })
        if (cursor) {
          query.set("cursor", cursor)
        }

        const response = await apiGet<ConversationMessagesResponse>(
          `/v1/conversations/${normalizedConversationId}/messages?${query.toString()}`
        )
        if (workspaceSessionVersion !== sessionVersion) {
          return
        }

        lastResponse = response.data
        items.push(...response.data.items)
        for (const [requestMessageId, turn] of Object.entries(response.data.turns ?? {})) {
          turnsByRequestMessageId.set(requestMessageId, turn)
        }

        if (!response.data.pagination.has_more) {
          break
        }

        cursor = response.data.pagination.next_cursor?.trim() || null
        if (!cursor) {
          break
        }
      }

      if (workspaceSessionVersion !== sessionVersion || lastResponse == null) {
        return
      }

      const current = getWorkspaceState()
      const existingConversation =
        current.conversations.find((conversation) => conversation.id === normalizedConversationId) ?? null
      const lastItem = items.at(-1) ?? null
      const conversationTitle =
        items.find((item) => item.role === "user" && item.message_type === "message")?.content ?? "Conversation"
      const baseConversation: Conversation = existingConversation
        ? {
            ...existingConversation,
            sourceFailedTurnId:
              lastResponse.source_failed_turn_id ?? existingConversation.sourceFailedTurnId ?? undefined,
            matchId: lastResponse.match_id ?? existingConversation.matchId ?? null,
            historyLoadStatus: "loaded",
          }
        : {
          id: normalizedConversationId,
          personId: lastResponse.subject_id,
          astrologerId: lastResponse.astrologer_id ?? getPlatformDefaultAstrologerId(current.astrologers) ?? "",
          astrologerDisplayName: lastResponse.astrologer_display_name,
          astrologerAvatarUrl: lastResponse.astrologer_avatar_url,
          astrologerSlug: lastResponse.astrologer_slug,
          sourceFailedTurnId: lastResponse.source_failed_turn_id ?? undefined,
          matchId: lastResponse.match_id ?? null,
          title: conversationTitle,
          summary: conversationTitle,
          updatedAt: lastItem?.created_at ?? nowLabel(),
          historyLoadStatus: "loaded",
          turns: [],
          composerDraft: "",
          composerSourceFailedTurnId: undefined,
        }
      const mergedConversation = mergeRefreshedConversation(
        baseConversation,
        lastResponse,
        items,
        turnsByRequestMessageId
      )
      if (!mergedConversation.turns.some((turn) => turn.status === "thinking" || turn.status === "streaming")) {
        for (const [localTurnId, submit] of pendingSocketSubmits.entries()) {
          if (submit.conversationId === normalizedConversationId) {
            pendingSocketSubmits.delete(localTurnId)
          }
        }
      }
      syncAssistantFeedbackConfirmedValuesFromConversation(mergedConversation)

      setState({
        ...current,
        conversations: existingConversation
          ? current.conversations.map((conversation) =>
              conversation.id === normalizedConversationId ? mergedConversation : conversation
            )
          : [
              mergedConversation,
              ...current.conversations.filter((conversation) => conversation.id !== normalizedConversationId),
            ],
        activeConversationId: options?.preserveActiveSelection
          ? current.activeConversationId
          : normalizedConversationId,
        selectedPersonId: options?.preserveActiveSelection
          ? current.selectedPersonId
          : current.people.find((person) => person.id === lastResponse.subject_id)?.id ?? current.selectedPersonId,
      })

      if (mergedConversation.turns.some((turn) => turn.status === "thinking" || turn.status === "streaming")) {
        connectWorkspaceSocket()
        flushPendingSocketSubmits()
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        await expireAuthenticatedSession()
        return
      }
      if (error instanceof ApiError && (error.status === 403 || error.status === 404)) {
        removeConversationFromState(normalizedConversationId)
        return
      }
      const currentOnError = getWorkspaceState()
      const existingOnError =
        currentOnError.conversations.find((conversation) => conversation.id === normalizedConversationId) ?? null
      if (existingOnError) {
        setState({
          ...currentOnError,
          conversations: currentOnError.conversations.map((conversation) =>
            conversation.id === normalizedConversationId
              ? { ...conversation, historyLoadStatus: "error" }
              : conversation
          ),
        })
      }
      throw error
    } finally {
      conversationMessageRequests.delete(normalizedConversationId)
    }
  })()

  conversationMessageRequests.set(normalizedConversationId, request)
  return request
}

function mapSubjectToPerson(subject: {
  subject_id: string
  owner_user_id: string
  name: string
  relationship_to_owner: string
  is_default: boolean
  birth_name: string
  birth_datetime: string
  birth_lat: number
  birth_lng: number
  birth_place: string
  birth_timezone: string
  birth_utc_offset?: string | null   // e.g. "+05:30" — stored at time of entry
  birth_time_source: string | null
  gender: string
  birth_time_lower?: string | null
  birth_time_upper?: string | null
  btr_verified?: boolean
  birth_time_tier?: number | null
  birth_time_status?: string | null
  birth_time_confidence_label?: string | null
  chart_usage_policy_summary?: {
    reading_mode?: string | null
    computed_varga_codes?: string[] | null
  } | null
  chart_summary?: PersonChartSummary | null
  created_at: string
  updated_at: string
}): Person {
  // Reconstruct local date/time using the stored UTC offset.
  // We never re-derive the offset from the timezone name — DST rules can change
  // over decades, making re-derivation unreliable for historical dates.
  let birthDate = ""
  let birthTime = ""
  try {
    const utcMs = new Date(subject.birth_datetime).getTime()
    const offset = subject.birth_utc_offset ?? null
    if (offset) {
      // Parse "+05:30" or "-04:00" into total minutes
      const m = offset.match(/^([+-])(\d{2}):(\d{2})$/)
      if (m) {
        const sign = m[1] === "+" ? 1 : -1
        const offsetMs = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60_000
        const local = new Date(utcMs + offsetMs)
        const yy = local.getUTCFullYear()
        const mo = String(local.getUTCMonth() + 1).padStart(2, "0")
        const dd = String(local.getUTCDate()).padStart(2, "0")
        birthDate = `${yy}-${mo}-${dd}`
        const hh = String(local.getUTCHours()).padStart(2, "0")
        const mm = String(local.getUTCMinutes()).padStart(2, "0")
        birthTime = `${hh}:${mm}`
      }
    }
    // Fallback for records saved before birth_utc_offset was introduced
    if (!birthDate) {
      const dt = new Date(subject.birth_datetime)
      birthDate = new Intl.DateTimeFormat("en-CA", {
        timeZone: subject.birth_timezone,
        year: "numeric", month: "2-digit", day: "2-digit",
      }).format(dt)
      const tp = new Intl.DateTimeFormat("en-US", {
        timeZone: subject.birth_timezone,
        hour: "numeric", minute: "2-digit", hour12: false,
      }).formatToParts(dt)
      const h = tp.find((p) => p.type === "hour")?.value ?? "0"
      const mm2 = tp.find((p) => p.type === "minute")?.value ?? "00"
      birthTime = `${(parseInt(h, 10) % 24).toString().padStart(2, "0")}:${mm2}`
    }
  } catch {
    birthDate = subject.birth_datetime.split("T")[0] ?? ""
    birthTime = subject.birth_datetime.includes("T")
      ? subject.birth_datetime.split("T")[1]?.slice(0, 5) ?? ""
      : ""
  }

  const birthTimeStatus = normalizeBirthTimeStatus(subject.birth_time_status)
  const btrVerified = Boolean(subject.btr_verified)

  // Derive tier from the stored window; if time is unknown there is no tier.
  let birthTimeTier =
    birthTimeStatus === "unknown"
      ? null
      : deriveBirthTimeTierFromWindow(subject.birth_time_lower, subject.birth_time_upper, btrVerified) ??
        (birthTimeStatus === "exact" ? 0 : 2)
  let birthTimeWindowFrom = ""
  let birthTimeWindowTo = ""

  if (
    birthTimeStatus !== "unknown" &&
    subject.birth_time_lower &&
    subject.birth_time_upper
  ) {
    const lowerParts = isoToLocalDateTimeParts(subject.birth_time_lower, subject.birth_timezone)
    const upperParts = isoToLocalDateTimeParts(subject.birth_time_upper, subject.birth_timezone)
    const midpointParts = isoToLocalDateTimeParts(
      midpointIso(subject.birth_time_lower, subject.birth_time_upper),
      subject.birth_timezone
    )
    const matchesCanonicalWindow =
      birthTimeTier !== null &&
      birthTimeTier >= 0 &&
      matchesCanonicalTierWindow(
        birthDate || midpointParts.date,
        birthTime || midpointParts.time,
        subject.birth_timezone,
        birthTimeTier,
        subject.birth_time_lower,
        subject.birth_time_upper
      )

    if (!matchesCanonicalWindow) {
      birthTimeTier = -1
      birthTimeWindowFrom = lowerParts.time
      birthTimeWindowTo = upperParts.time
      birthTime = midpointParts.time
    }
  }

  return {
    id: subject.subject_id,
    name: subject.name,
    relationshipToOwner: subject.relationship_to_owner,
    birthName: subject.birth_name,
    birthDate,
    birthTime,
    birthTimeWindowFrom,
    birthTimeWindowTo,
    birthPlace: subject.birth_place,
    birthLat: subject.birth_lat,
    birthLng: subject.birth_lng,
    birthTimezone: subject.birth_timezone,
    birthUtcOffset: subject.birth_utc_offset ?? null,
    gender: subject.gender,
    birthTimeTier,
    birthTimeStatus,
    birthTimeConfidence: subject.birth_time_confidence_label ?? (btrVerified ? "BTR verified" : "Birth time window saved"),
    birthTimeSource: subject.birth_time_source ?? "Entered manually",
    computedCharts: subject.chart_usage_policy_summary?.computed_varga_codes ?? [],
    readingMode: subject.chart_usage_policy_summary?.reading_mode ?? "",
    avatarColor: pickPersonColor(subject.subject_id),
    notes: btrVerified ? "BTR verified" : "",
    isDefault: subject.is_default,
    chartSummary: subject.chart_summary ?? null,
  }
}

function mapSubjectCatalog(items: SubjectCatalogItem[]): Person[] {
  return items.map(mapSubjectToPerson)
}

function normalizeBirthTimeStatus(value: string | null | undefined): BirthTimeStatus {
  if (value === "approximate") return "approximate"
  if (value === "unknown") return "unknown"
  return "exact"
}

function pickPersonColor(seed: string) {
  const palette = ["#7c6ef0", "#e8609a", "#2db87f", "#f07c3a", "#3a9ef0", "#c060e8", "#e0b020"]
  const hash = [...seed].reduce((total, char) => total + char.charCodeAt(0), 0)
  return palette[hash % palette.length] ?? palette[0]
}

function pickAstrologerColor(seed: string) {
  const palette = ["#b47a28", "#6b69d6", "#e45b8f", "#2db87f"]
  const hash = [...seed].reduce((total, char) => total + char.charCodeAt(0), 0)
  return palette[hash % palette.length] ?? palette[0]
}
